// GtInsights: Insights on generativetalent.co for the team (C1-INSIGHTS2, 1 Nov 2026). Autopilot: C1-INSIGHTS14.
// LinkedIn posts per profile: C1-LINKEDIN4. Insights by email and the list at /insights/list: C1-NEWS.
//
// /insights            every article: The Agent Workforce (weekly) and the AI Workforce Index (monthly),
//                      by state; start a new one
// /insights/:postId    one article: write and edit it, add your own paragraph and sources,
//                      then publish now, schedule it, or take it offline
//
// Publishing warns and never blocks, except trainer or human data wording, which never goes live.
import { Fragment, useState, useEffect, useCallback, useMemo } from 'react'; // Fragment: C1-LIEXTRA4
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthFetch } from './GtLexaAdmin';
import gtConfirm from '../../components/GtConfirm';
import GtEditor from '../../components/GtEditor';
import { GtSelect, GtDatePicker } from '../../components/GtControls';

const STATE = {
  queued: { label: 'Queued', tone: 'slate' }, writing: { label: 'Writing', tone: 'blue' }, draft: { label: 'Draft', tone: 'amber' },
  scheduled: { label: 'Scheduled', tone: 'blue' }, published: { label: 'Live', tone: 'green' }, offline: { label: 'Offline', tone: 'slate' },
  failed: { label: 'Writing failed', tone: 'rose' },
};
const FILTERS = [['all', 'All'], ['draft', 'Drafts'], ['scheduled', 'Scheduled'], ['published', 'Live'], ['offline', 'Offline']];
const HOURS = Array.from({ length: 15 }, (_, i) => { const h = String(i + 7).padStart(2, '0'); return { value: h + ':00', label: h + ':00' }; });

function ukTime(v) {
  if (!v) return '';
  try { return new Date(v).toLocaleString('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch (e) { return ''; }
}
function ukDay(v) {
  if (!v) return '';
  try { return new Date(v).toLocaleDateString('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short', year: 'numeric' }); } catch (e) { return ''; }
}
function monthLabel(ym) {
  const m = String(ym || '').match(/^(\d{4})-(\d{2})$/);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) : ym;
}
const FIELD = { title: 'Title', standfirst: 'Standfirst', body: 'Article', jimmy_note: 'Our view' }; // C1-INSIGHTS4
function Pill({ s }) { const x = STATE[s] || { label: s, tone: 'slate' }; return <span className={'gtjf-pill gtjf-pill--' + x.tone}>{x.label}</span>; }

// ---------------------------------------------------------------- autopilot (C1-INSIGHTS14)
// Monday 06:00 London the next role is written. A clean draft goes live the next morning at 09:00 unless it
// is held. Anything flagged waits for you. The Index follows the same rule on the 2nd of the month.
const LONDON = 'Europe/London';
function londonParts(d) {
  return Object.fromEntries(new Intl.DateTimeFormat('en-GB', { timeZone: LONDON, weekday: 'short', hour: '2-digit', hour12: false }).formatToParts(d).map((x) => [x.type, x.value]));
}
function dayTile(v) {
  const d = new Date(v);
  return {
    wd: d.toLocaleDateString('en-GB', { timeZone: LONDON, weekday: 'short' }),
    day: d.toLocaleDateString('en-GB', { timeZone: LONDON, day: 'numeric' }),
    mon: d.toLocaleDateString('en-GB', { timeZone: LONDON, month: 'short' }),
    time: d.toLocaleTimeString('en-GB', { timeZone: LONDON, hour: '2-digit', minute: '2-digit' }),
  };
}
function whenLong(v) {
  if (!v) return '';
  const d = new Date(v);
  return d.toLocaleDateString('en-GB', { timeZone: LONDON, weekday: 'long', day: 'numeric', month: 'long' }) + ' at ' + d.toLocaleTimeString('en-GB', { timeZone: LONDON, hour: '2-digit', minute: '2-digit' });
}
// The Mondays the next roles are written on: this Monday if it is before 06:00, otherwise next week onwards.
function mondays(n) {
  const now = new Date();
  const p = londonParts(now);
  const dow = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(p.weekday);
  const first = dow === 0 && Number(p.hour) < 6 ? 0 : 7 - dow;
  return Array.from({ length: n }, (_, i) => new Date(now.getTime() + (first + i * 7) * 86400000));
}
function roleLine(p) { return p.role ? p.role.title + (p.role.family ? ' · ' + p.role.family : '') : p.period ? monthLabel(p.period) : ''; }

function Tile({ tone, top, big, low }) {
  return <span className={'gtins-tile gtins-tile--' + tone}><em>{top}</em><b>{big}</b><span>{low}</span></span>;
}

function Autopilot({ authFetch, navigate, series, posts, reload, showDrafts, on }) {
  const [next, setNext] = useState(null);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const loadQueue = useCallback(() => (series === 'weekly' ? authFetch('/api/insights/admin/queue?limit=5').then((x) => setNext(x.next || [])).catch((e) => setMsg(e.message)) : Promise.resolve()), [authFetch, series]);
  useEffect(() => { loadQueue(); }, [loadQueue]);
  const mine = (posts || []).filter((p) => p.series === series);
  const writingNow = mine.some((p) => p.status === 'writing');
  useEffect(() => { if (!writingNow) return undefined; const t = setInterval(reload, 5000); return () => clearInterval(t); }, [writingNow, reload]);

  const live = mine.filter((p) => p.status === 'scheduled').sort((a, b) => new Date(a.publish_at) - new Date(b.publish_at));
  const writing = mine.filter((p) => p.status === 'writing' || p.status === 'failed');
  const waiting = mine.filter((p) => p.status === 'draft');
  const dates = mondays(5);

  async function hold(p) {
    if (!(await gtConfirm({ title: 'Hold this article?', message: 'It stays here as a draft and does not go live until you publish it.', confirmLabel: 'Hold it' }))) return;
    setBusy('hold' + p.id); setMsg('');
    try { await authFetch('/api/insights/admin/' + p.id + '/publish', { method: 'POST', body: JSON.stringify({ published: false }) }); await reload(); } catch (e) { setMsg(e.message); } finally { setBusy(''); }
  }
  async function writeNow(r) {
    if (!(await gtConfirm({ title: 'Write ' + r.title + ' now?', message: 'It takes about a minute. If nothing is flagged it goes live tomorrow at 09:00, unless you hold it.', confirmLabel: 'Write it now' }))) return;
    setBusy('w' + r.id); setMsg('');
    try { const x = await authFetch('/api/insights/admin/queue/' + r.id + '/write', { method: 'POST', body: '{}' }); navigate('/insights/' + x.id); } catch (e) { setMsg(e.message); setBusy(''); }
  }
  async function skip(r) {
    setBusy('s' + r.id); setMsg('');
    try { await authFetch('/api/insights/admin/queue/' + r.id + '/skip', { method: 'POST', body: '{}' }); await loadQueue(); } catch (e) { setMsg(e.message); } finally { setBusy(''); }
  }

  const steps = series === 'weekly'
    ? ['Written Monday 06:00', 'Read it or hold it', 'Live Tuesday 09:00']
    : ['Jobs counted on the 1st', 'Written on the 2nd', 'Live the 3rd at 09:00'];
  const upNext = series === 'weekly' && next && next.length ? next[0] : null;
  const after = series === 'weekly' && next ? next.slice(1, 5) : [];
  let indexNext = null;
  if (series === 'index') {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1);
    let ym = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
    if (mine.some((p) => p.period === ym)) { const t = new Date(); ym = t.getFullYear() + '-' + String(t.getMonth() + 1).padStart(2, '0'); }
    const [y, m] = ym.split('-').map(Number);
    indexNext = { ym, on: new Date(y, m, 2, 7) };
  }

  return (
    <>
      <section className="gtins-auto">
        <header className="gtins-auto-h">
          {on ? <span className="gtins-auto-on"><i />On autopilot</span> : <span className="gtins-auto-on gtins-auto-on--off"><i />Autopilot is off here<em>It runs on work.gtco.io only. Buttons still work.</em></span>}
          <ol className="gtins-auto-steps">{steps.map((t, i) => <li key={i}><span>{i + 1}</span>{t}</li>)}</ol>
        </header>
        <div className="gtins-auto-rows">
          {live.map((p) => {
            const t = dayTile(p.publish_at);
            return (
              <div key={p.id} className="gtins-ar">
                <Tile tone="green" top="Goes live" big={t.wd + ' ' + t.day + ' ' + t.mon} low={t.time} />
                <span className="gtins-ar-t"><b>{p.title}</b><span>{roleLine(p)} · {p.words} words</span><span className="gtins-ar-hint">Do nothing and it publishes itself.</span></span>
                <span className="gtins-ar-a">
                  <button type="button" className="gt-btn" onClick={() => navigate('/insights/' + p.id)}>Read it</button>
                  <button type="button" className="gt-btn" disabled={!!busy} onClick={() => hold(p)}>{busy === 'hold' + p.id ? 'Holding…' : 'Hold'}</button>
                </span>
              </div>
            );
          })}
          {writing.map((p) => (
            <div key={p.id} className="gtins-ar">
              {p.status === 'failed' ? <Tile tone="rose" top="Stopped" big="Failed" low="Try again" /> : <Tile tone="blue" top="Now" big="Writing" low="About a minute" />}
              <span className="gtins-ar-t"><b>{p.series === 'weekly' && p.role ? p.role.title : p.title}</b><span>{p.status === 'failed' ? 'The writer stopped. Open it and press Rewrite.' : 'Being written from the role and fresh sources.'}</span></span>
              <span className="gtins-ar-a"><button type="button" className="gt-btn" onClick={() => navigate('/insights/' + p.id)}>Open</button></span>
            </div>
          ))}
          {waiting.slice(0, 2).map((p) => (
            <div key={p.id} className="gtins-ar gtins-ar--wait">
              {p.flags.length ? <Tile tone="amber" top="Waiting" big="Needs you" low={p.flags.length + (p.flags.length === 1 ? ' check' : ' checks')} /> : <Tile tone="slate" top="Waiting" big="Held" low="Your call" />}
              <span className="gtins-ar-t"><b>{p.title}</b><span>{roleLine(p)} · {p.words} words</span>
                <span className="gtins-ar-hint">{p.flags.length ? 'Something was flagged, so it will not go live by itself.' : 'It will not go live until you publish it.'}</span></span>
              <span className="gtins-ar-a"><button type="button" className="gt-btn gt-btn--primary" onClick={() => navigate('/insights/' + p.id)}>Open it</button></span>
            </div>
          ))}
          {waiting.length > 2 && <button type="button" className="gtiv-link gtins-auto-more" onClick={showDrafts}>{waiting.length - 2} more {waiting.length - 2 === 1 ? 'draft' : 'drafts'} waiting</button>}
          {upNext && (() => {
            const t = dayTile(dates[0]);
            return (
              <div className="gtins-ar gtins-ar--next">
                <Tile tone="ink" top="Next" big={t.wd + ' ' + t.day + ' ' + t.mon} low="06:00" />
                <span className="gtins-ar-t"><b>{upNext.title}</b><span>{upNext.family_name}</span><span className="gtins-ar-hint">Written by itself, then live the next morning at 09:00.</span></span>
                <span className="gtins-ar-a">
                  <button type="button" className="gt-btn" disabled={!!busy} onClick={() => writeNow(upNext)}>{busy === 'w' + upNext.id ? 'Starting…' : 'Write it now'}</button>
                  <button type="button" className="gtiv-link gtins-ar-skip" disabled={!!busy} onClick={() => skip(upNext)}>Skip</button>
                </span>
              </div>
            );
          })()}
          {series === 'weekly' && next && !next.length && <p className="gtivl-muted gtins-auto-empty">Every role in the library has an article. Nothing left to write.</p>}
          {indexNext && (() => {
            const t = dayTile(indexNext.on);
            return (
              <div className="gtins-ar gtins-ar--next">
                <Tile tone="ink" top="Next" big={t.wd + ' ' + t.day + ' ' + t.mon} low="07:00" />
                <span className="gtins-ar-t"><b>AI Workforce Index, {monthLabel(indexNext.ym)}</b><span>From that month's job postings, with its charts</span><span className="gtins-ar-hint">Written by itself once the month's postings are counted, then live the next morning at 09:00.</span></span>
              </div>
            );
          })()}
        </div>
        {msg && <p className="gtiv-err gtjf-msg">{msg}</p>}
      </section>
      {after.length > 0 && (
        <section className="gtins-up">
          <div className="gtins-up-h"><b>After that</b><span>In this order. Skip any you do not want.</span></div>
          <div className="gtins-up-row">
            {after.map((r, i) => {
              const t = dayTile(dates[i + 1]);
              return (
                <div key={r.id} className="gtins-up-c">
                  <span className="gtins-up-n">{t.wd + ' ' + t.day + ' ' + t.mon}</span>
                  <b>{r.title}</b>
                  <em>{r.family_name}</em>
                  <span className="gtins-up-a">
                    <button type="button" className="gtiv-link" disabled={!!busy} onClick={() => writeNow(r)}>{busy === 'w' + r.id ? 'Starting…' : 'Write now'}</button>
                    <button type="button" className="gtiv-link gtins-up-skip" disabled={!!busy} onClick={() => skip(r)}>Skip</button>
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </>
  );
}

// C1-INSIGHTS7: the monthly demand sweep behind the Index. Runs by itself on the 1st; the Index is written on the 2nd.
function DemandSweep({ authFetch, navigate, posts }) {
  const [d, setD] = useState(null);
  const [month, setMonth] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState('');
  const load = useCallback(() => authFetch('/api/insights/admin/demand').then((x) => { setD(x); setMonth((m) => m || x.last_month); }).catch((e) => setMsg(e.message)), [authFetch]);
  useEffect(() => { load(); }, [load]);
  const running = d && (d.running || (d.state && d.state.status === 'running'));
  useEffect(() => { if (!running) return undefined; const t = setInterval(load, 5000); return () => clearInterval(t); }, [running, load]);
  const months = useMemo(() => {
    const out = []; const x = new Date(); x.setDate(1);
    for (let i = 0; i < 13; i++) { const ym = x.getFullYear() + '-' + String(x.getMonth() + 1).padStart(2, '0'); out.push({ value: ym, label: monthLabel(ym) }); x.setMonth(x.getMonth() - 1); }
    return out;
  }, []);
  async function run() {
    setMsg('');
    try { await authFetch('/api/insights/admin/demand/run', { method: 'POST', body: JSON.stringify({ month }) }); await load(); } catch (e) { setMsg(e.message); }
  }
  async function writeIndex() {
    const have = (posts || []).find((p) => p.series === 'index' && p.period === month);
    if (have) { navigate('/insights/' + have.id); return; }
    setBusy('index'); setMsg('');
    try { const r = await authFetch('/api/insights/admin', { method: 'POST', body: JSON.stringify({ series: 'index', period: month }) }); navigate('/insights/' + r.id); } catch (e) { setMsg(e.message); setBusy(''); }
  }
  if (!d) return null;
  const s = d.state;
  const swept = d.months.some((m) => m.month === month);
  const have = (posts || []).find((p) => p.series === 'index' && p.period === month);
  return (
    <section className="gtiv-card gtins-next">
      <div className="gtins-next-h"><b>Demand sweep</b><span>On the 1st of each month every Hiring now and Emerging role is searched for job postings from the month just gone. On the 2nd, the Index is written from it and goes live the next morning unless you hold it. Run it by hand here if a month was missed.</span></div>
      {running ? (
        <div className="gtjf-prog"><div className="gtjf-bar"><i style={{ width: (s && s.total ? Math.round((s.done / s.total) * 100) : 5) + '%' }} /></div><span>Sweeping {monthLabel(s && s.month)}: {s ? s.done : 0} of {s ? s.total : '…'} roles. You can leave this screen.</span></div>
      ) : s && s.status === 'failed' ? <p className="gtiv-err gtjf-msg">The last sweep failed: {s.error}</p> : null}
      {d.months.length > 0 && (
        <ul className="gtins-sweeps">{d.months.slice(0, 6).map((m) => <li key={m.month}><b>{monthLabel(m.month)}</b><span>{m.postings} postings found across {m.roles} roles</span></li>)}</ul>
      )}
      <div className="gtins-new-f">
        <GtSelect value={month} options={months} onChange={setMonth} width={220} />
        <button type="button" className="gt-btn" disabled={running} onClick={run}>{swept ? 'Run the sweep again' : 'Run the sweep'}</button>
        <button type="button" className="gt-btn gt-btn--primary" disabled={running || !!busy || (!swept && !have)} title={!swept && !have ? 'Run the sweep for this month first' : ''} onClick={writeIndex}>{have ? 'Open the ' + monthLabel(month) + ' Index' : busy ? 'Starting…' : 'Write the ' + monthLabel(month) + ' Index'}</button>
      </div>
      {msg && <p className="gtiv-err gtjf-msg">{msg}</p>}
    </section>
  );
}

// ---------------------------------------------------------------- LinkedIn (C1-LINKEDIN; several profiles C1-LINKEDIN4)
// Every article gets a LinkedIn post on each profile switched on, each written separately so they never read
// alike. The first goes out when the article goes live, each next profile a few hours later. The article goes
// with each post as a link card.
const LI_STATE = {
  writing: { label: 'Writing', tone: 'blue' }, draft: { label: 'Ready', tone: 'green' }, needs_you: { label: 'Needs you', tone: 'amber' },
  held: { label: 'Held', tone: 'slate' }, posting: { label: 'Posting', tone: 'blue' }, posted: { label: 'Posted', tone: 'green' },
  failed: { label: 'Did not go out', tone: 'rose' }, skipped: { label: 'Skipped', tone: 'slate' },
};
function initials(name) { return String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join(''); }
function Avatar({ a, size }) {
  const s = size || 40;
  return a && a.picture
    ? <img className="gtli-av" src={a.picture} alt="" width={s} height={s} style={{ width: s, height: s }} />
    : <span className="gtli-av gtli-av--i" style={{ width: s, height: s, fontSize: Math.max(10, Math.round(s * 0.34)) }}>{initials(a && a.name)}</span>;
}
function daysLeft(v) { return v ? Math.ceil((new Date(v).getTime() - Date.now()) / 86400000) : null; }
function firstName(n) { return String(n || '').split(' ')[0]; }
function nth(i) { return ['1st', '2nd', '3rd'][i] || (i + 1) + 'th'; }
function andList(xs) { return xs.length < 2 ? xs.join('') : xs.slice(0, -1).join(', ') + ' and ' + xs[xs.length - 1]; }
function connLine(a) {
  const left = daysLeft(a.expires_at);
  const who = a.kind === 'organization' ? 'a page admin' : firstName(a.name); // C1-LIPAGE
  if (a.last_error) return { warn: true, text: 'LinkedIn turned the connection down. Send ' + who + ' a new connect link.' };
  if (left != null && left <= 0) return { warn: true, text: 'The connection has run out. Send ' + who + ' a new connect link.' };
  return { warn: left != null && left <= 7, text: 'Connected until ' + ukDay(a.expires_at) + (left != null && left <= 7 ? ', ' + left + (left === 1 ? ' day' : ' days') + ' left. Send a new connect link.' : '') };
}

function useLinkedInStatus(authFetch) {
  const [s, setS] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(() => authFetch('/api/linkedin/admin/status').then((x) => { setS(x); setErr(''); return x; }).catch((e) => setErr(e.message)), [authFetch]);
  useEffect(() => { load(); }, [load]);
  return [s, setS, load, err];
}

function LinkedInBar({ authFetch }) {
  const [s, setS, , err] = useLinkedInStatus(authFetch);
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  if (!s) return err ? <div className="gtiv-card gtli-bar"><span className="gtli-bar-t"><b>LinkedIn</b><span>{err}</span></span></div> : null;
  const accounts = s.accounts || [];
  const posting = accounts.filter((a) => a.is_author);
  const gap = s.gap_hours != null ? s.gap_hours : 3;
  const warn = !s.configured || !posting.length || posting.some((a) => connLine(a).warn);

  async function makeLink(app) {
    setBusy(app === 'page' ? 'page' : 'link'); setMsg('');
    try { const r = await authFetch('/api/linkedin/admin/invite', { method: 'POST', body: JSON.stringify(app === 'page' ? { app: 'page' } : {}) }); setLink(Object.assign({ app: app === 'page' ? 'page' : 'member' }, r)); } catch (e) { setMsg(e.message); } finally { setBusy(''); }
  }
  async function copy() {
    try { await navigator.clipboard.writeText(link.url); setMsg('Copied. Send it to the person whose profile should post.'); } catch (e) { setMsg('Select the link and copy it.'); }
  }
  async function setAuto(on) {
    setBusy('auto'); setMsg('');
    try { setS(await authFetch('/api/linkedin/admin/settings', { method: 'POST', body: JSON.stringify({ autopost: on }) })); } catch (e) { setMsg(e.message); } finally { setBusy(''); }
  }
  async function setPosting(a, on) {
    setBusy('p' + a.id); setMsg('');
    try { setS(await authFetch('/api/linkedin/admin/accounts/' + a.id + '/posting', { method: 'POST', body: JSON.stringify({ on }) })); } catch (e) { setMsg(e.message); } finally { setBusy(''); }
  }
  async function disconnect(a) {
    if (!(await gtConfirm({ title: 'Disconnect ' + a.name + '?', message: 'Nothing more goes out on ' + firstName(a.name) + "'s profile until they connect again. Posts already on LinkedIn stay there.", confirmLabel: 'Disconnect', tone: 'danger' }))) return;
    setBusy('off' + a.id); setMsg('');
    try { setS(await authFetch('/api/linkedin/admin/accounts/' + a.id + '/remove', { method: 'POST', body: '{}' })); } catch (e) { setMsg(e.message); } finally { setBusy(''); }
  }

  let title;
  let sub;
  if (!s.configured) { title = 'Not set up on the server yet'; sub = 'The backend needs its three LinkedIn settings before anyone can connect.'; }
  else if (!accounts.length) { title = 'No profile connected'; sub = 'Posts are written once someone connects. Make a link and send it to them.'; }
  else if (!posting.length) { title = 'No profile is posting'; sub = 'Switch a profile on below and posts go out on it.'; }
  else if (posting.length === 1) { title = 'Posting as ' + posting[0].name; sub = 'Each article, and the two extra posts a week, go on this profile. Connect another profile and it posts too, with its own wording.'; }
  else { title = 'Posting as ' + andList(posting.map((a) => a.name)); sub = 'Each article, and the two extra posts a week, go on every profile switched on, each with its own wording, ' + gap + (gap === 1 ? ' hour' : ' hours') + ' apart in this order.'; }

  return (
    <section className={'gtiv-card gtli-bar' + (warn ? ' gtli-bar--amber' : '')}>
      <div className="gtli-bar-row">
        {posting.length ? <span className="gtli-stack">{posting.slice(0, 3).map((a) => <Avatar key={a.id} a={a} size={40} />)}</span> : <span className="gtli-av gtli-av--empty" style={{ width: 44, height: 44 }} />}
        <span className="gtli-bar-t"><em>LinkedIn</em><b>{title}</b><span>{sub}</span></span>
        {s.configured && (
          <span className="gtli-bar-a">
            {posting.length > 0 && (
              <span className="gtli-seg" role="group" aria-label="Post by itself">
                <button type="button" className={'gtli-seg-b' + (s.autopost ? ' gtli-seg-b--on' : '')} disabled={!!busy} onClick={() => !s.autopost && setAuto(true)}>Posts by itself</button>
                <button type="button" className={'gtli-seg-b' + (!s.autopost ? ' gtli-seg-b--on' : '')} disabled={!!busy} onClick={() => s.autopost && setAuto(false)}>I press Post</button>
              </span>
            )}
            <button type="button" className={'gt-btn' + (!accounts.length ? ' gt-btn--primary' : '')} disabled={!!busy} onClick={() => makeLink()}>{busy === 'link' ? 'Making…' : accounts.length ? 'Connect a profile' : 'Make a connect link'}</button>
            {s.page_configured && !accounts.some((a) => a.kind === 'organization') && <button type="button" className="gt-btn" disabled={!!busy} onClick={() => makeLink('page')}>{busy === 'page' ? 'Making…' : 'Connect the company page'}</button>}{/* C1-LIPAGE */}
          </span>
        )}
      </div>
      {accounts.length > 0 && (
        <div className="gtli-accs">
          {accounts.map((a) => {
            const c = connLine(a);
            const rank = posting.findIndex((x) => x.id === a.id);
            return (
              <div key={a.id} className={'gtli-acc' + (a.is_author ? '' : ' gtli-acc--off')}>
                <Avatar a={a} size={34} />
                <span className="gtli-acc-t"><b>{a.name}{a.is_author && posting.length > 1 && <i className="gtli-acc-n">{nth(rank)}{rank > 0 ? ', ' + (rank * gap) + 'h after' : ', at go-live'}</i>}</b>
                  <span className={c.warn ? 'gtli-acc-warn' : ''}>{c.text}</span></span>
                <span className="gtli-seg" role="group" aria-label={'Posts on ' + a.name}>
                  <button type="button" className={'gtli-seg-b' + (a.is_author ? ' gtli-seg-b--on' : '')} disabled={!!busy} onClick={() => !a.is_author && setPosting(a, true)}>Posts</button>
                  <button type="button" className={'gtli-seg-b' + (!a.is_author ? ' gtli-seg-b--on' : '')} disabled={!!busy} onClick={() => a.is_author && setPosting(a, false)}>Off</button>
                </span>
                <button type="button" className="gtiv-link gtli-off" disabled={!!busy} onClick={() => disconnect(a)}>Disconnect</button>
              </div>
            );
          })}
        </div>
      )}
      {s.configured && posting.length > 0 && !s.autopilot && <p className="gtli-note">Nothing posts by itself here. It runs on work.gtco.io only. Post now still works and goes to the real LinkedIn profile.</p>}
      {link && (
        <div className="gtli-link">
          <input className="gtiv-in" readOnly value={link.url} onFocus={(e) => e.target.select()} aria-label="Connect link" />
          <button type="button" className="gt-btn gt-btn--primary" onClick={copy}>Copy link</button>
          <span>{link.app === 'page' ? <>Works once, until {ukDay(link.expires_at)}. Open it signed in to LinkedIn as an admin of the company page and press Allow. The page then posts first, before each profile.</> : <>Works once, until {ukDay(link.expires_at)}. Whoever opens it signs in to LinkedIn and presses Allow, and their profile starts posting. Nothing else to set up.</>}</span>
        </div>
      )}
      {msg && <p className="gtli-note">{msg}</p>}
    </section>
  );
}

// The post as it will look in the feed: name, text folded after a few lines, the article card.
function FeedPreview({ account, text, hashtags, article, when }) {
  const [more, setMore] = useState(false);
  const tags = (hashtags || []).map((t) => '#' + t).join(' ');
  const long = String(text || '').length > 260;
  return (
    <div className="gtli-feed">
      <div className="gtli-feed-h">
        <Avatar a={account || { name: 'Not connected' }} size={42} />
        <span className="gtli-feed-hn"><b>{account ? account.name : 'No profile connected'}</b><em>{when ? ukDay(when) : 'Now'} · Public</em></span>
      </div>
      <div className={'gtli-feed-t' + (long && !more ? ' gtli-feed-t--fold' : '')}>
        {String(text || '').split(/\n\n+/).map((para, i) => <p key={i}>{para}</p>)}
        {tags && <p className="gtli-feed-tags">{tags}</p>}
      </div>
      {long && <button type="button" className="gtli-feed-more" onClick={() => setMore((m) => !m)}>{more ? 'show less' : '…see more'}</button>}
      <div className="gtli-feed-card">
        <span className="gtli-feed-pill">{article.series === 'index' ? 'AI WORKFORCE INDEX' : 'INSIGHTS'}</span>
        <b>{article.title}</b>
        <span className="gtli-feed-by">Generative Talent Co{article.role ? ', on ' + article.role.title : ''}</span>
      </div>
      <div className="gtli-feed-lnk"><b>{article.title}</b><span>generativetalent.co</span></div>{/* C1-LINKEDIN2: link card, clicking opens the article */}
    </div>
  );
}

function tabLine(x) {
  const d = x.post;
  if (!d) return x.account.posting ? 'Not written yet' : 'Off';
  if (d.status === 'posted') return 'Posted ' + ukDay(d.posted_at);
  if (d.status === 'draft' && x.due_at) return 'Ready, goes out ' + dayTile(x.due_at).time;
  return (LI_STATE[d.status] || { label: d.status }).label;
}

// C1-LINKEDIN4: one tab per profile; each profile's post has its own state and buttons.
function LinkedInPost({ authFetch, article }) {
  const [s, , , sErr] = useLinkedInStatus(authFetch);
  const [list, setList] = useState(undefined);
  const [pick, setPick] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(() => authFetch('/api/linkedin/admin/post/' + article.id).then((x) => { setList(x.posters || []); setErr(''); return x.posters; }).catch((e) => setErr(e.message)), [authFetch, article.id]);
  useEffect(() => { load(); }, [load]);
  const moving = (list || []).some((x) => x.post && (x.post.status === 'writing' || x.post.status === 'posting'));
  useEffect(() => { if (!moving) return undefined; const t = setInterval(load, 3000); return () => clearInterval(t); }, [moving, load]);

  if (list === undefined || !s) return sErr || err ? <section className="gtiv-card gtins-card gtli"><span className="gtins-k">LinkedIn post</span><p className="gtiv-err gtjf-msg">{sErr || err}</p></section> : null;
  const cur = list.find((x) => x.account.id === pick) || list[0] || null;
  return (
    <section className="gtiv-card gtins-card gtli">
      <span className="gtins-k">{list.length > 1 ? 'LinkedIn posts' : 'LinkedIn post'}<em>{list.length > 1 ? 'One per profile, each with its own wording. The article goes with each as a link card.' : 'The article goes with it as a link card. Clicking the picture opens it.'}</em></span>
      {!cur ? (
        <div className="gtli-empty"><span>No LinkedIn profile is posting. Connect a profile, or switch one on, on the Insights screen.</span></div>
      ) : (
        <>
          {list.length > 1 && (
            <div className="gtli-tabs" role="tablist" aria-label="Profiles">
              {list.map((x) => {
                const tone = x.post ? (LI_STATE[x.post.status] || { tone: 'slate' }).tone : 'slate';
                return (
                  <button key={x.account.id} type="button" role="tab" aria-selected={x === cur} className={'gtli-tab' + (x === cur ? ' gtli-tab--on' : '')} onClick={() => setPick(x.account.id)}>
                    <Avatar a={x.account} size={28} />
                    <span className="gtli-tab-t"><b>{x.account.name}</b><em><i className={'gtli-dot gtli-dot--' + tone} />{tabLine(x)}</em></span>
                  </button>
                );
              })}
            </div>
          )}
          <PosterPost key={cur.account.id} authFetch={authFetch} article={article} s={s} entry={cur} load={load} setList={setList} />
        </>
      )}
    </section>
  );
}

function PosterPost({ authFetch, article, s, entry, load, setList }) {
  const d = entry.post;
  const account = entry.account;
  const [f, setF] = useState(d ? { text: d.text, hashtags: (d.hashtags || []).join(', ') } : null);
  const [edit, setEdit] = useState(false);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [note, setNote] = useState('');
  const [needsDelete, setNeedsDelete] = useState(null); // C1-LINKEDIN3: the old post could not be taken down
  const dText = d ? d.text : '';
  const dTags = d ? (d.hashtags || []).join(', ') : '';
  useEffect(() => { if (!edit) setF({ text: dText, hashtags: dTags }); }, [dText, dTags, edit]);

  const live = article.status === 'published';
  const dirty = d && f && (f.text !== d.text || f.hashtags !== (d.hashtags || []).join(', '));
  const st = d ? LI_STATE[d.status] || { label: d.status, tone: 'slate' } : null;
  const whose = account.name + "'s";
  const api = '/api/linkedin/admin/post/' + article.id;

  async function run(action, body) {
    setBusy(action); setMsg('');
    try {
      const r = await authFetch(api + '/' + action, { method: 'POST', body: JSON.stringify(Object.assign({ account_id: account.id }, body || {})) });
      if (r.posters) setList(r.posters); else await load();
      if (action === 'post') setMsg(r.ok ? 'Posted.' + (r.note ? ' ' + r.note : '') : r.message);
      if (action === 'write') { setNote(''); setEdit(false); setTimeout(load, 800); }
      return r;
    } catch (e) { setMsg(e.message); return null; } finally { setBusy(''); }
  }
  async function save() {
    setBusy('save'); setMsg('');
    try {
      const r = await authFetch(api, { method: 'PATCH', body: JSON.stringify({ account_id: account.id, text: f.text, hashtags: f.hashtags }) });
      if (r.posters) setList(r.posters);
      setEdit(false); setMsg('Saved.');
      return true;
    } catch (e) { setMsg(e.message); return false; } finally { setBusy(''); }
  }
  async function postNow() {
    if (dirty && !(await save())) return;
    const warn = (d.flags || []).filter((x) => x.code !== 'trainer');
    const lines = ['It goes on ' + whose + ' LinkedIn profile now, with the article as a link card.'];
    if (warn.length) lines.push(warn.length + (warn.length === 1 ? ' flag is' : ' flags are') + ' worth a look first: ' + warn.map((x) => x.text).join(' · '));
    if (!(await gtConfirm({ title: 'Post to LinkedIn?', message: lines, confirmLabel: 'Post it' }))) return;
    run('post');
  }
  async function rewrite() {
    if (d && d.text && !(await gtConfirm({ title: 'Rewrite the LinkedIn post?', message: 'It is written again from the article for ' + whose + ' profile. Your edits to this post are replaced.', confirmLabel: 'Rewrite it' }))) return;
    run('write', { note: note.trim() || null });
  }
  // C1-LINKEDIN3: take the post on LinkedIn down, write it again with the current instructions, post it.
  async function postAgain(skip) {
    const lines = skip
      ? ['The post is written again with the current instructions and goes out as a new post on ' + whose + ' LinkedIn profile.', 'Only do this once the old post is gone from LinkedIn, or both will show.']
      : ['The post on ' + whose + ' LinkedIn profile now is taken down. The post is then written again with the current instructions and goes out as a new post.', 'Likes and comments on the old post go with it.'];
    if (!(await gtConfirm({ title: 'Post this again?', message: lines, confirmLabel: skip ? 'Post again' : 'Take it down and post again', tone: 'danger' }))) return;
    setBusy('again'); setMsg('');
    try {
      const r = await authFetch(api + '/again', { method: 'POST', body: JSON.stringify({ account_id: account.id, note: note.trim() || null, skip_delete: !!skip }) });
      if (r.posters) setList(r.posters);
      if (r.ok) {
        setNeedsDelete(null); setNote('');
        setMsg((r.removed === 'deleted' ? 'The old post is down. ' : '') + 'The new post is being written and goes out by itself once it is ready. If anything is flagged, it waits here for you.');
        setTimeout(load, 800);
      } else { setNeedsDelete(r.url || ''); setMsg(r.message); }
    } catch (e) { setMsg(e.message); } finally { setBusy(''); }
  }

  let hint = '';
  if (d) {
    const due = entry.due_at ? new Date(entry.due_at) : null;
    if (d.status === 'draft') {
      hint = !s.autopilot ? 'Nothing posts by itself here. Press Post now to send it.'
        : !s.autopost ? 'Posting by itself is off. Press Post now when you are ready.'
          : !account.connected ? 'This profile is no longer connected. Send a new connect link.'
            : !account.posting ? 'This profile is switched off, so this does not go out by itself. Press Post now to send it.'
              : live && (!due || due <= new Date()) ? 'Goes out by itself within a minute.'
                : due ? 'Goes out by itself ' + whenLong(due) + '.' : 'Goes out by itself when the article goes live.';
    } else if (d.status === 'needs_you') hint = 'Something was flagged, so it will not go out by itself. Fix it, or press Post now.';
    else if (d.status === 'held') hint = 'Held. It does not go out until you press Post now or Let it post.';
    else if (d.status === 'skipped') hint = 'Skipped. Nothing goes on ' + whose + ' profile for this article.';
    else if (d.status === 'failed') hint = d.error || 'It did not go out.';
    else if (d.status === 'writing') hint = 'Being written from the article. About half a minute.';
    else if (d.status === 'posting') hint = 'Sending to LinkedIn now.';
    else if (d.status === 'posted') hint = 'Posted ' + ukTime(d.posted_at) + ' as ' + (d.account_name || account.name) + (d.posted_by && d.posted_by !== 'Automatic' ? ' by ' + d.posted_by : ', by itself') + '.';
  }
  const canPost = d && ['draft', 'needs_you', 'held', 'failed'].includes(d.status);
  const trainer = d && (d.flags || []).some((x) => x.code === 'trainer');

  if (!d) {
    return (
      <div className="gtli-empty">
        <span>{!account.posting ? 'Off for ' + account.name + '. Nothing goes on this profile.' : article.status === 'published' || article.status === 'scheduled' ? 'Not written yet for ' + account.name + '. It is written by itself before the article goes live, or now if you want to read it first.' : 'Written for ' + account.name + ' once the article is scheduled. You can write it now to read it first.'}</span>
        {account.connected && <button type="button" className="gt-btn" disabled={!!busy || !String(article.body || '').trim()} onClick={() => run('write')}>{busy === 'write' ? 'Starting…' : 'Write it now'}</button>}
        {msg && <p className="gtli-note">{msg}</p>}
      </div>
    );
  }
  return (
    <>
      <div className={'gtli-state gtli-state--' + st.tone}>
        <span className={'gtjf-pill gtjf-pill--' + st.tone}>{st.label}</span>
        <span className="gtli-state-t">{hint}</span>
        {d.status === 'posted' && d.url && <a className="gt-btn" href={d.url} target="_blank" rel="noopener noreferrer">Open on LinkedIn &#8599;</a>}
      </div>
      {d.status === 'posted' && d.error && <p className="gtli-note gtli-note--amber">{d.error}</p>}

      {d.status === 'writing' ? <div className="gtins-writing gtli-writing"><span className="gtins-dot" />Writing the LinkedIn post…</div> : edit && canPost ? (
        <div className="gtli-edit">
          <textarea className="gtiv-in gtins-ta gtli-ta" rows={14} value={f.text} onChange={(e) => setF(Object.assign({}, f, { text: e.target.value }))} aria-label="Post text" />
          <label className="gtins-field"><span className="gtins-k">Hashtags<em>Up to three, separated by commas</em></span>
            <input className="gtiv-in" value={f.hashtags} onChange={(e) => setF(Object.assign({}, f, { hashtags: e.target.value }))} /></label>
          <span className="gtli-count">{(f.text || '').trim().length.toLocaleString('en-GB')} characters before hashtags · LinkedIn shows about the first 210 before "see more"</span>
        </div>
      ) : <FeedPreview account={d.status === 'posted' && d.account_name ? Object.assign({}, account, { name: d.account_name }) : account} text={d.text} hashtags={d.hashtags} article={article} when={d.status === 'posted' ? d.posted_at : null} />}

      {(d.flags || []).length > 0 && d.status !== 'posted' && (
        <ul className="gtjf-flags gtins-flags">{d.flags.map((x, i) => <li key={i} className={x.code === 'trainer' ? 'gtjf-flag--stop' : ''}>{x.text}</li>)}</ul>
      )}

      {canPost && (
        <div className="gtli-acts">
          {edit
            ? <><button type="button" className="gt-btn" disabled={!!busy || !dirty} onClick={save}>{busy === 'save' ? 'Saving…' : 'Save'}</button><button type="button" className="gtiv-link" onClick={() => { setEdit(false); setF({ text: d.text, hashtags: (d.hashtags || []).join(', ') }); }}>Cancel</button></>
            : <button type="button" className="gt-btn" disabled={!!busy} onClick={() => setEdit(true)}>Edit</button>}
          <button type="button" className="gt-btn gt-btn--primary" disabled={!!busy || !live || trainer || !account.connected} title={!live ? 'The article is not live yet' : !account.connected ? 'This profile is no longer connected' : trainer ? 'Remove the trainer or human data wording first' : ''} onClick={postNow}>{busy === 'post' ? 'Posting…' : 'Post now'}</button>
          {d.status === 'held' ? <button type="button" className="gtiv-link" disabled={!!busy} onClick={() => run('release')}>Let it post</button>
            : <button type="button" className="gtiv-link" disabled={!!busy} onClick={() => run('hold')}>Hold</button>}
          <button type="button" className="gtiv-link gtli-skip" disabled={!!busy} onClick={() => run('skip')}>Skip</button>
        </div>
      )}
      {d.status === 'skipped' && <div className="gtli-acts"><button type="button" className="gt-btn" disabled={!!busy} onClick={() => run('release')}>Bring it back</button></div>}
      {canPost && !edit && (
        <div className="gtli-rw">
          <input className="gtiv-in" placeholder="Note for the writer (optional), for example: shorter, open on the hiring point" value={note} onChange={(e) => setNote(e.target.value)} aria-label="Note for the writer" />
          <button type="button" className="gt-btn" disabled={!!busy} onClick={rewrite}>{busy === 'write' ? 'Starting…' : 'Rewrite'}</button>
        </div>
      )}
      {d.status === 'posted' && (
        <div className="gtli-rw">{/* C1-LINKEDIN3 */}
          <input className="gtiv-in" placeholder="Note for the writer (optional), for example: open on the hiring point" value={note} onChange={(e) => setNote(e.target.value)} aria-label="Note for the writer" />
          <button type="button" className="gt-btn" disabled={!!busy || !live || !account.connected} title={!live ? 'The article is not live' : !account.connected ? 'This profile is no longer connected' : 'Takes this post down on LinkedIn and posts it again, written with the current instructions'} onClick={() => postAgain(false)}>{busy === 'again' ? 'Starting…' : 'Post again'}</button>
        </div>
      )}
      {d.status === 'posted' && needsDelete !== null && (
        <div className="gtli-acts">
          {needsDelete && <a className="gt-btn" href={needsDelete} target="_blank" rel="noopener noreferrer">Open the old post &#8599;</a>}
          <button type="button" className="gt-btn gt-btn--primary" disabled={!!busy} onClick={() => postAgain(true)}>I deleted it, post again</button>
        </div>
      )}
      {msg && <p className="gtli-note">{msg}</p>}
    </>
  );
}

// ---------------------------------------------------------------- LinkedIn between articles (C1-LIEXTRA, c83)
// Two extra posts a week, Wednesday and Thursday at 09:00: a library role, a live job and an Index chart take turns.
// Same pieces as the article LinkedIn card: feed preview, Edit, Post now, Hold, Skip, Rewrite, one tab per profile.
const LX_KIND = {
  library: { label: 'Library role', tone: 'blue', pill: 'NEW JOB FAMILIES', site: 'generativetalent.co' },
  job: { label: 'Live job', tone: 'green', pill: 'LIVE ROLE', site: 'jobs.gtco.io' },
  chart: { label: 'Index chart', tone: 'slate', pill: 'AI WORKFORCE INDEX', site: 'generativetalent.co' },
};
function lxDay(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
  return m ? new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12)).toLocaleDateString('en-GB', { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long' }) : '';
}

function lxWho(p) { return p.account_kind === 'organization' ? 'Company page' : String(p.account_name || '').split(' ')[0]; }
const LX_RANK = ['needs_you', 'failed', 'held', 'writing', 'posting', 'draft', 'posted', 'skipped'];
function lxTile(group) {
  const worst = group.posts.map((p) => p.status).sort((x, y) => LX_RANK.indexOf(x) - LX_RANK.indexOf(y))[0];
  const first = group.posts.map((p) => p.due_at).filter(Boolean).sort()[0];
  const t = first ? dayTile(first) : null;
  const flags = group.posts.reduce((n, p) => n + (p.status === 'needs_you' ? (p.flags || []).length : 0), 0);
  if (worst === 'needs_you') return { status: worst, tile: <Tile tone="amber" top="Waiting" big="Needs you" low={flags ? flags + (flags === 1 ? ' check' : ' checks') : 'Your call'} /> };
  if (worst === 'failed') return { status: worst, tile: <Tile tone="rose" top="Stopped" big="Failed" low="Try again" /> };
  if (worst === 'held') return { status: worst, tile: <Tile tone="slate" top="Waiting" big="Held" low="Your call" /> };
  if (worst === 'writing') return { status: worst, tile: <Tile tone="blue" top="Now" big="Writing" low="About a minute" /> };
  if (worst === 'posting') return { status: worst, tile: <Tile tone="blue" top="Now" big="Posting" low="A moment" /> };
  if (worst === 'posted') { const at = group.posts.map((p) => p.posted_at).filter(Boolean).sort()[0]; const u = at ? dayTile(at) : t; return { status: worst, tile: <Tile tone="green" top="Went out" big={u ? u.wd + ' ' + u.day + ' ' + u.mon : ''} low={u ? u.time : ''} /> }; }
  if (worst === 'skipped') return { status: worst, tile: <Tile tone="slate" top="Skipped" big={t ? t.wd + ' ' + t.day + ' ' + t.mon : ''} low="Not posted" /> };
  return { status: worst, tile: <Tile tone="green" top="Goes out" big={t ? t.wd + ' ' + t.day + ' ' + t.mon : ''} low={t ? t.time : ''} /> };
}

// C1-INSTABS: an article going out on LinkedIn, shown in Coming up. Its posts are read and edited on the article page.
function LinkedInArticleRow({ a, sep, navigate }) {
  const posts = (a.posts || []).map((p) => Object.assign({}, p, { status: p.status === 'unwritten' ? 'draft' : p.status }));
  const x = lxTile({ posts });
  const who = (a.posts || []).map((p) => lxWho(p) + (p.status === 'posted' ? ' posted' : p.status === 'unwritten' ? ' not written yet' : p.due_at ? ' ' + dayTile(p.due_at).time : '')).join(' · ');
  const hint = ['needs_you', 'failed'].includes(x.status) ? 'Something needs you. Open the article to see it.' : 'Goes out with the article. Read or change it on the article page.';
  return (
    <div className={'gtins-ar' + (['needs_you', 'failed', 'held'].includes(x.status) ? ' gtins-ar--wait' : '')} style={sep}>
      {x.tile}
      <span className="gtins-ar-t"><b>Article: {a.title}</b><span>{who}</span><span className="gtins-ar-hint">{hint}</span></span>
      <span className="gtins-ar-a"><button type="button" className={'gt-btn' + (['needs_you', 'failed'].includes(x.status) ? ' gt-btn--primary' : '')} onClick={() => navigate('/insights/' + a.id)}>{['needs_you', 'failed'].includes(x.status) ? 'Open it' : 'Read it'}</button></span>
    </div>
  );
}

// C1-INSTABS: every LinkedIn post, articles and extra posts alike, newest first, 50 at a time.
const LH_STATUS = [['all', 'All'], ['posted', 'Posted'], ['booked', 'Booked'], ['skipped', 'Skipped'], ['failed', 'Did not go out']];
const LH_KIND = { article: 'Article', library: 'Library role', job: 'Live job', chart: 'Index chart' };
function LinkedInHistory({ authFetch, d, setD, navigate, preset }) {
  const [status, setStatus] = useState('all');
  const [kind, setKind] = useState('');
  const [acct, setAcct] = useState('');
  const [month, setMonth] = useState('');
  const [q, setQ] = useState('');
  const [dq, setDq] = useState('');
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [months, setMonths] = useState([]);
  const [open, setOpen] = useState(null);
  const [more, setMore] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { const t = setTimeout(() => setDq(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => {
    if (!preset || !preset.n) return;
    setStatus(preset.status);
    const el = document.getElementById('gtli-history');
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [preset]);
  const query = useCallback((offset) => authFetch('/api/linkedin/extra/admin/history?' + new URLSearchParams({ status, kind, account: acct, month, q: dq, offset: String(offset || 0) }).toString()), [authFetch, status, kind, acct, month, dq]);
  const ver = d ? d.slots.map((g) => g.key + ':' + g.posts.map((p) => p.status).join(',')).join('|') : '';
  useEffect(() => {
    let live = true;
    query(0).then((x) => { if (!live) return; setRows(x.rows || []); setTotal(x.total || 0); setAccounts(x.accounts || []); setMonths(x.months || []); setErr(''); }).catch((e) => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [query, ver]);
  async function showMore() {
    setMore(true);
    try { const x = await query(rows.length); setRows(rows.concat(x.rows || [])); } catch (e) { setErr(e.message); } finally { setMore(false); }
  }
  const filtered = status !== 'all' || kind || acct || month || dq;
  function when(r) {
    if (r.status === 'posted') return 'Posted ' + ukTime(r.posted_at);
    if (r.status === 'skipped') return 'Skipped';
    if (r.status === 'failed') return 'Did not go out';
    if (r.due_at) return 'Goes out ' + whenLong(r.due_at);
    if (r.at) return (new Date(r.at) > new Date() ? 'Goes out with the article, ' : 'With the article, ') + ukDay(r.at);
    return '';
  }
  return (
    <section id="gtli-history">
      <h2 className="gtins-all">History</h2>
      <div className="gtjf-bar2">
        <div className="gtivl-filters" role="tablist">
          {LH_STATUS.map(([k, l]) => <button key={k} type="button" role="tab" aria-selected={status === k} className={'gtivl-filter' + (status === k ? ' gtivl-filter--on' : '')} onClick={() => setStatus(k)}>{l}</button>)}
        </div>
        <input className="gtiv-in gtjf-search" type="search" placeholder="Search posts" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search posts" />
      </div>
      <div className="gtins-new-f" style={{ margin: '0 0 12px' }}>
        <GtSelect value={kind} options={[{ value: '', label: 'Every kind' }].concat(Object.keys(LH_KIND).map((k) => ({ value: k, label: LH_KIND[k] })))} onChange={setKind} width={180} />
        <GtSelect value={acct} options={[{ value: '', label: 'Every profile' }].concat(accounts.map((a) => ({ value: String(a.id), label: a.name })))} onChange={setAcct} width={220} />
        <GtSelect value={month} options={[{ value: '', label: 'Any month' }].concat(months.map((m) => ({ value: m, label: monthLabel(m) })))} onChange={setMonth} width={180} />
      </div>
      {err && <div className="gtiv-card gtiv-err">{err}</div>}
      {rows === null ? <div className="gtiv-card gtiv-loading">Loading the history…</div> : rows.length === 0 ? (
        <div className="gtiv-card gtivl-empty"><b>{filtered ? 'Nothing matches' : 'Nothing here yet'}</b><span>{filtered ? 'Try another filter.' : 'Every LinkedIn post shows here once it is written.'}</span></div>
      ) : (
        <div className="gtivl-list">
          {rows.map((r) => {
            const st = LI_STATE[r.status] || { label: r.status, tone: 'slate' };
            const isOpen = open === r.id;
            const g = r.kind !== 'article' && d ? d.slots.find((x) => x.item_ref === r.item_ref) : null;
            const live = g && g.posts.some((p) => !['posted', 'skipped'].includes(p.status));
            return (
              <Fragment key={r.id}>
                <button type="button" className="gtiv-card gtivl-row gtins-row" aria-expanded={r.kind === 'article' ? undefined : isOpen} onClick={() => (r.kind === 'article' ? navigate('/insights/' + r.insights_post_id) : setOpen(isOpen ? null : r.id))}>
                  <span className="gtivl-row-t">
                    <b>{(LH_KIND[r.kind] || r.kind) + ': ' + (r.title || '')}</b>
                    <span>{(r.account_kind === 'organization' ? 'Company page' : r.account_name || '') + (r.adhoc ? ' · extra post' : '')}</span>
                    <span>{when(r)}</span>
                  </span>
                  <span className={'gtjf-pill gtjf-pill--' + st.tone}>{st.label}</span>
                </button>
                {isOpen && (
                  <section className="gtiv-card gtins-card gtli">
                    {live ? <LinkedInExtraItem group={g} d={d} authFetch={authFetch} setD={setD} /> : (
                      <>
                        <LinkedInExtraPreview p={r} group={{ date: r.slot_date, link_title: r.title, link_url: r.link_url }} k={LX_KIND[r.kind] || { pill: '', site: '' }} authFetch={authFetch} />
                        <div className="gtli-acts">
                          {r.url && <a className="gt-btn" href={r.url} target="_blank" rel="noopener noreferrer">Open on LinkedIn &#8599;</a>}
                          {r.link_url && <a className="gtiv-link" href={r.link_url} target="_blank" rel="noopener noreferrer">Open the page it links to &#8599;</a>}
                        </div>
                      </>
                    )}
                  </section>
                )}
              </Fragment>
            );
          })}
        </div>
      )}
      {rows && rows.length < total && <button type="button" className="gt-btn" style={{ marginTop: 12 }} disabled={more} onClick={showMore}>{more ? 'Loading…' : 'Show more (' + (total - rows.length) + ' left)'}</button>}
    </section>
  );
}

// C1-INSTABS: every Insights email, newest first. Each opens its article, where the email card lives.
function EmailSends({ authFetch, navigate }) {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [more, setMore] = useState(false);
  const [err, setErr] = useState('');
  const query = useCallback((offset) => authFetch('/api/newsletter/sends?offset=' + (offset || 0)), [authFetch]);
  useEffect(() => { query(0).then((x) => { setRows(x.sends || []); setTotal(x.total || 0); }).catch((e) => setErr(e.message)); }, [query]);
  async function showMore() {
    setMore(true);
    try { const x = await query(rows.length); setRows(rows.concat(x.sends || [])); } catch (e) { setErr(e.message); } finally { setMore(false); }
  }
  return (
    <section>
      <h2 className="gtins-all">Every send</h2>
      {err && <div className="gtiv-card gtiv-err">{err}</div>}
      {rows === null ? <div className="gtiv-card gtiv-loading">Loading the sends…</div> : rows.length === 0 ? (
        <div className="gtiv-card gtivl-empty"><b>No emails yet</b><span>The first one goes out when the next article goes live.</span></div>
      ) : (
        <div className="gtivl-list">
          {rows.map((s) => {
            const st = NL_STATE[s.status] || { label: s.status, tone: 'slate' };
            const line = s.status === 'sent'
              ? 'Sent ' + ukTime(s.sent_at) + ' to ' + nf(s.total) + ' · ' + nf(s.delivered) + ' delivered · ' + nf(s.clicked) + ' clicked' + (s.bounced ? ' · ' + nf(s.bounced) + ' bounced' : '') + (s.unsubscribed ? ' · ' + nf(s.unsubscribed) + ' unsubscribed' : '')
              : st.label + (s.updated_at ? ', ' + ukDay(s.updated_at) : '');
            return (
              <button key={s.id} type="button" className="gtiv-card gtivl-row gtins-row" onClick={() => navigate('/insights/' + s.post_id)}>
                <span className="gtivl-row-t"><b>{s.subject || s.title}</b><span>{s.title}</span><span>{line}</span></span>
                <span className={'gtjf-pill gtjf-pill--' + st.tone}>{st.label}</span>
              </button>
            );
          })}
        </div>
      )}
      {rows && rows.length < total && <button type="button" className="gt-btn" style={{ marginTop: 12 }} disabled={more} onClick={showMore}>{more ? 'Loading…' : 'Show more (' + (total - rows.length) + ' left)'}</button>}
    </section>
  );
}

// C1-INSTABS: Articles, LinkedIn and Email, in the same switcher as Nathan, Lexa and Beth.
const INS_TABS = [['articles', 'Articles', '/insights'], ['linkedin', 'LinkedIn', '/insights/linkedin'], ['email', 'Email', '/insights/email']];
function InsightsTabs({ tab, navigate, d, attn }) {
  const drafts = d ? d.posts.filter((p) => p.status === 'draft').length : 0;
  const sub = {
    articles: drafts ? drafts + (drafts === 1 ? ' draft' : ' drafts') : 'weekly and monthly',
    linkedin: attn && attn.linkedin ? attn.linkedin + ' need you' : 'posts and history',
    email: attn && attn.email ? attn.email + ' need you' : 'sends and the list',
  };
  return (
    <div className="gtlx-switch" role="tablist" aria-label="Insights">
      {INS_TABS.map(([k, l, path]) => (
        <button key={k} type="button" role="tab" aria-selected={tab === k} className={'gtlx-sw' + (tab === k ? ' gtlx-sw--on' : '')} style={{ paddingLeft: 14 }} onClick={() => navigate(path)}>{l}<em>{sub[k]}</em></button>
      ))}
    </div>
  );
}

function LinkedInExtra({ authFetch, navigate }) { // C1-LIEXTRA4: the same panel and rows as the article autopilot. C1-INSTABS: Coming up and History
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [hist, setHist] = useState({ status: 'all', n: 0 }); // C1-INSTABS: History filter set from Coming up
  const [arts, setArts] = useState([]);
  const [openKey, setOpenKey] = useState(null);
  const [xKind, setXKind] = useState(''); // C1-LIEXTRA7: an extra post on top of the schedule
  const [xDay, setXDay] = useState('');
  const [warn, setWarn] = useState('');
  const load = useCallback(() => authFetch('/api/linkedin/extra/admin').then((x) => { setD(x); setErr(''); return x; }).catch((e) => setErr(e.message)), [authFetch]);
  useEffect(() => { load(); }, [load]);
  const moving = !!(d && d.slots.some((g) => g.posts.some((p) => p.status === 'writing' || p.status === 'posting')));
  useEffect(() => { if (!moving) return undefined; const t = setInterval(load, 3000); return () => clearInterval(t); }, [moving, load]);
  const loadArts = useCallback(() => authFetch('/api/linkedin/extra/admin/articles-ahead').then((x) => setArts((x && x.articles) || [])).catch(() => {}), [authFetch]);
  useEffect(() => { loadArts(); }, [loadArts]);

  if (!d) return err ? <section className="gtins-auto"><div className="gtins-auto-h"><span className="gtins-auto-on gtins-auto-on--off"><i />Extra LinkedIn posts<em>{err}</em></span></div></section> : null;

  async function setOn(on) {
    setBusy('on'); setMsg('');
    try { setD(await authFetch('/api/linkedin/extra/admin/settings', { method: 'POST', body: JSON.stringify({ on }) })); } catch (e) { setMsg(e.message); } finally { setBusy(''); }
  }
  async function planNow(kind) {
    setBusy('plan' + (kind || '')); setMsg('');
    try {
      const r = await authFetch('/api/linkedin/extra/admin/plan', { method: 'POST', body: JSON.stringify(kind ? { kind } : {}) });
      setD(r.overview);
      const g = (r.overview.slots || []).find((x) => x.posts.some((p) => (r.ids || []).includes(p.id)));
      if (g) setOpenKey(g.key);
    } catch (e) { setMsg(e.message); } finally { setBusy(''); }
  }

  const kindOpts = Object.keys(LX_KIND).filter((k) => !d.available || d.available[k] !== false).map((k) => ({ value: k, label: LX_KIND[k].label }));
  const xk = kindOpts.some((o) => o.value === xKind) ? xKind : (kindOpts[0] || {}).value || '';
  const dayOpts = (() => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(d.today || ''));
    if (!m) return [];
    return Array.from({ length: 30 }, (_, i) => {
      const t = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + i, 12));
      const v = t.toISOString().slice(0, 10);
      return { value: v, label: i === 0 ? 'Today' : i === 1 ? 'Tomorrow, ' + lxDay(v) : lxDay(v) };
    });
  })();
  const xd = dayOpts.some((o) => o.value === xDay) ? xDay : d.today;
  async function planExtra() {
    setBusy('extra'); setMsg(''); setWarn('');
    try {
      const r = await authFetch('/api/linkedin/extra/admin/extra', { method: 'POST', body: JSON.stringify({ kind: xk, date: xd }) });
      setD(r.overview);
      const g = (r.overview.slots || []).find((x) => x.posts.some((p) => (r.ids || []).includes(p.id)));
      if (g) setOpenKey(g.key);
      if (r.warning) setWarn(r.warning);
    } catch (e) { setMsg(e.message); } finally { setBusy(''); }
  }

  const next = d.next;
  // C1-INSTABS: Coming up holds the next two weeks only; anything booked later is one line that opens History.
  const horizon = (() => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(d.today || '')); return m ? new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + 14, 12)).toISOString().slice(0, 10) : '9999-12-31'; })();
  const upcoming = d.slots.filter((g) => g.posts.some((p) => !['posted', 'skipped'].includes(p.status))).sort((a, b) => String(a.date).localeCompare(String(b.date)));
  const later = upcoming.filter((g) => String(g.date) > horizon);
  const items = upcoming.filter((g) => String(g.date) <= horizon).map((g) => ({ date: String(g.date), g }))
    .concat(arts.filter((a) => String(a.date) <= horizon).map((a) => ({ date: String(a.date), a })))
    .sort((x, y) => x.date.localeCompare(y.date));
  const auto = d.autopilot && d.on;
  const nk = next ? LX_KIND[next.kind] || { label: next.kind } : null;
  const nt = next ? dayTile(next.at) : null;
  let prevOpen = false;

  return (
    <>
    <section className="gtins-auto">
      <header className="gtins-auto-h">
        {auto ? <span className="gtins-auto-on"><i />Extra LinkedIn posts on autopilot</span>
          : !d.on ? <span className="gtins-auto-on gtins-auto-on--off"><i />Extra LinkedIn posts are off<em>Articles still post as usual.</em></span>
            : <span className="gtins-auto-on gtins-auto-on--off"><i />Extra LinkedIn posts: autopilot is off here<em>It runs on work.gtco.io only. Buttons still work.</em></span>}
        <ol className="gtins-auto-steps">{['Written the day before', 'Read it or hold it', 'Out Wed and Thu 09:00'].map((t, i) => <li key={i}><span>{i + 1}</span>{t}</li>)}</ol>
        <span className="gtli-seg" role="group" aria-label="Extra LinkedIn posts">
          <button type="button" className={'gtli-seg-b' + (d.on ? ' gtli-seg-b--on' : '')} disabled={!!busy} onClick={() => !d.on && setOn(true)}>On</button>
          <button type="button" className={'gtli-seg-b' + (!d.on ? ' gtli-seg-b--on' : '')} disabled={!!busy} onClick={() => d.on && setOn(false)}>Off</button>
        </span>
      </header>
      <div className="gtins-auto-rows">
        {items.map((it) => {
          if (it.a) { const sepA = prevOpen ? { borderTop: '1px solid var(--gt-line-soft)' } : undefined; prevOpen = false; return <LinkedInArticleRow key={'a' + it.a.id} a={it.a} sep={sepA} navigate={navigate} />; }
          const g = it.g;
          const k = LX_KIND[g.kind] || { label: g.kind, pill: '', site: '' };
          const x = lxTile(g);
          const open = openKey === g.key;
          const who = (g.adhoc ? 'Extra post · ' : '') + g.posts.map((p) => lxWho(p) + (p.status === 'posted' ? ' posted' : p.due_at ? ' ' + dayTile(p.due_at).time : '')).join(' · ');
          const hint = x.status === 'needs_you' ? 'Something was flagged, so it will not go out by itself.'
            : x.status === 'failed' ? 'It did not go out. Open it to see why.'
              : x.status === 'held' ? 'It will not go out until you let it post.'
                : x.status === 'posted' ? 'Gone out. Open it to see the posts on LinkedIn.'
                  : x.status === 'skipped' ? 'Skipped. Nothing went out.'
                    : x.status === 'draft' ? (auto && d.autopost ? 'Do nothing and it goes out by itself.' : 'Nothing goes out by itself here. Press Post now when you are ready.')
                      : '';
          const sep = prevOpen ? { borderTop: '1px solid var(--gt-line-soft)' } : undefined;
          prevOpen = open;
          return (
            <Fragment key={g.key}>
              <div className={'gtins-ar' + (['needs_you', 'failed', 'held'].includes(x.status) ? ' gtins-ar--wait' : '')} style={sep}>
                {x.tile}
                <span className="gtins-ar-t"><b>{k.label}: {g.link_title}</b><span>{who}</span>{hint && <span className="gtins-ar-hint">{hint}</span>}</span>
                <span className="gtins-ar-a">
                  <button type="button" className={'gt-btn' + (!open && ['needs_you', 'failed'].includes(x.status) ? ' gt-btn--primary' : '')} aria-expanded={open} onClick={() => setOpenKey(open ? null : g.key)}>{open ? 'Close' : ['needs_you', 'failed'].includes(x.status) ? 'Open it' : 'Read it'}</button>
                </span>
              </div>
              {open && (
                <div className="gtins-card gtli" style={{ borderTop: '1px solid var(--gt-line-soft)' }}>
                  <span className="gtins-k">{k.label}<em><a href={g.link_url} target="_blank" rel="noopener noreferrer">Open the page it links to &#8599;</a></em></span>
                  <LinkedInExtraItem group={g} d={d} authFetch={authFetch} setD={setD} />
                </div>
              )}
            </Fragment>
          );
        })}
        {later.length > 0 && (
          <p className="gtivl-muted gtins-auto-empty" style={prevOpen ? { borderTop: '1px solid var(--gt-line-soft)' } : undefined}>{later.length} more booked, up to {lxDay(later[later.length - 1].date)}.{' '}
            <button type="button" className="gtiv-link" onClick={() => setHist({ status: 'booked', n: hist.n + 1 })}>See them in History</button></p>
        )}
        {next && (() => {
          const sep = prevOpen ? { borderTop: '1px solid var(--gt-line-soft)' } : undefined;
          return (
            <div className="gtins-ar gtins-ar--next" style={sep}>
              <Tile tone="ink" top="Next" big={nt.wd + ' ' + nt.day + ' ' + nt.mon} low="09:00" />
              <span className="gtins-ar-t">
                <b>{next.article_day ? 'Nothing extra: an article goes out that day' : next.kind ? nk.label : 'Nothing new to post right now'}</b>{/* C1-LIEXTRA5 */}
                <span>A library role, a live job and an Index chart take turns. Or write one now:{' '}
                  {Object.keys(LX_KIND).map((kk, i) => <span key={kk}>{i ? ', ' : ''}{d.available && d.available[kk] === false ? <span className="gtins-ar-hint" title={d.unavailable[kk]}>{LX_KIND[kk].label.toLowerCase()} ({{ library: 'none left', job: 'none new', chart: 'no live Index yet' }[kk]})</span> : <button type="button" className="gtiv-link" disabled={!!busy || !d.posters.length} onClick={() => planNow(kk)}>{busy === 'plan' + kk ? 'starting…' : LX_KIND[kk].label.toLowerCase()}</button>}{/* C1-LIEXTRA6 */}</span>)}
                </span>
                <span className="gtins-ar-hint">{!d.posters.length ? 'No LinkedIn profile is posting. Switch one on in the LinkedIn bar above.' : 'Written by itself the day before at 09:00, then out on the day at 09:00.'}</span>
              </span>
              <span className="gtins-ar-a">
                <button type="button" className="gt-btn" disabled={!!busy || !d.posters.length || !next.kind} title={!next.kind ? 'Nothing new to post right now' : ''} onClick={() => planNow(null)}>{busy === 'plan' ? 'Starting…' : 'Write it now'}</button>
              </span>
            </div>
          );
        })()}
        {!next && <p className="gtivl-muted gtins-auto-empty">No free Wednesday or Thursday was found.</p>}
        <div className="gtins-ar gtins-ar--next">{/* C1-LIEXTRA7 */}
          <Tile tone="ink" top="Extra" big="Any day" low="On top" />
          <span className="gtins-ar-t">
            <b>An extra post, on top of Wednesday and Thursday</b>
            <div className="gtins-new-f">
              <GtSelect value={xk} options={kindOpts} onChange={setXKind} width={200} />
              <GtSelect value={xd} options={dayOpts} onChange={setXDay} width={260} />
            </div>
            <span className="gtins-ar-hint">{xd === d.today ? 'Goes out an hour after it is written, so there is time to read it or hold it.' : 'Goes out at 09:00 that day.'} It does not use up a Wednesday or Thursday.{Object.keys(LX_KIND).filter((k) => d.available && d.available[k] === false).map((k) => ' ' + LX_KIND[k].label + ': ' + d.unavailable[k]).join('')}</span>
          </span>
          <span className="gtins-ar-a">
            <button type="button" className="gt-btn" disabled={!!busy || !d.posters.length || !xk} onClick={planExtra}>{busy === 'extra' ? 'Starting…' : 'Write it'}</button>
          </span>
        </div>
      </div>
      {warn && <p className="gtli-note gtli-note--amber" style={{ margin: '0 20px 14px' }}>{warn}</p>}
      {msg && <p className="gtiv-err gtjf-msg">{msg}</p>}
    </section>
    <LinkedInHistory authFetch={authFetch} d={d} setD={setD} navigate={navigate} preset={hist} />
    </>
  );
}

// The open post: one tab per profile, then that profile's post.
function LinkedInExtraItem({ group, d, authFetch, setD }) {
  const [pick, setPick] = useState(null);
  const cur = group.posts.find((p) => p.id === pick) || group.posts[0];
  const k = LX_KIND[group.kind] || { label: group.kind, pill: '', site: '' };
  return (
    <>
      {group.posts.length > 1 && (
        <div className="gtli-tabs" role="tablist" aria-label="Profiles">
          {group.posts.map((p) => {
            const tone = (LI_STATE[p.status] || { tone: 'slate' }).tone;
            const tl = p.status === 'posted' ? 'Posted ' + ukDay(p.posted_at) : p.status === 'draft' && p.due_at ? 'Ready, goes out ' + dayTile(p.due_at).time : (LI_STATE[p.status] || { label: p.status }).label;
            return (
              <button key={p.id} type="button" role="tab" aria-selected={p === cur} className={'gtli-tab' + (p === cur ? ' gtli-tab--on' : '')} onClick={() => setPick(p.id)}>
                <Avatar a={{ name: p.account_name, picture: p.account_picture }} size={28} />
                <span className="gtli-tab-t"><b>{p.account_name}</b><em><i className={'gtli-dot gtli-dot--' + tone} />{tl}</em></span>
              </button>
            );
          })}
        </div>
      )}
      {cur && <LinkedInExtraPost key={cur.id} p={cur} group={group} k={k} d={d} authFetch={authFetch} setD={setD} />}
    </>
  );
}

function LinkedInExtraPost({ p, group, k, d, authFetch, setD }) {
  const [f, setF] = useState({ text: p.text, hashtags: (p.hashtags || []).join(', ') });
  const [edit, setEdit] = useState(false);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [note, setNote] = useState('');
  const pTags = (p.hashtags || []).join(', ');
  useEffect(() => { if (!edit) setF({ text: p.text, hashtags: pTags }); }, [p.text, pTags, edit]);

  const api = '/api/linkedin/extra/admin/post/' + p.id;
  const whose = (p.account_name || 'this profile') + "'s";
  const dirty = f.text !== p.text || f.hashtags !== pTags;
  const st = LI_STATE[p.status] || { label: p.status, tone: 'slate' };
  const canPost = ['draft', 'needs_you', 'held', 'failed'].includes(p.status);
  const trainer = (p.flags || []).some((x) => x.code === 'trainer');

  async function run(action, body) {
    setBusy(action); setMsg('');
    try {
      const r = await authFetch(api + '/' + action, { method: 'POST', body: JSON.stringify(body || {}) });
      if (r.overview) setD(r.overview);
      if (action === 'post') setMsg(r.ok ? 'Posted.' + (r.note ? ' ' + r.note : '') : r.message);
      if (action === 'write') { setNote(''); setEdit(false); }
      return r;
    } catch (e) { setMsg(e.message); return null; } finally { setBusy(''); }
  }
  async function save() {
    setBusy('save'); setMsg('');
    try {
      const r = await authFetch(api, { method: 'PATCH', body: JSON.stringify({ text: f.text, hashtags: f.hashtags }) });
      if (r.overview) setD(r.overview);
      setEdit(false); setMsg('Saved.');
      return true;
    } catch (e) { setMsg(e.message); return false; } finally { setBusy(''); }
  }
  async function postNow() {
    if (dirty && !(await save())) return;
    const warn = (p.flags || []).filter((x) => x.code !== 'trainer');
    const lines = ['It goes on ' + whose + ' LinkedIn now, with the page as a link card.'];
    if (warn.length) lines.push(warn.length + (warn.length === 1 ? ' flag is' : ' flags are') + ' worth a look first: ' + warn.map((x) => x.text).join(' · '));
    if (!(await gtConfirm({ title: 'Post to LinkedIn?', message: lines, confirmLabel: 'Post it' }))) return;
    run('post');
  }
  async function rewrite() {
    if (p.text && !(await gtConfirm({ title: 'Rewrite the LinkedIn post?', message: 'It is written again for ' + whose + ' LinkedIn. Your edits to this post are replaced.', confirmLabel: 'Rewrite it' }))) return;
    run('write', { note: note.trim() || null });
  }

  let hint = '';
  if (p.status === 'draft') {
    hint = !d.autopilot ? 'Nothing posts by itself here. Press Post now to send it.'
      : !d.autopost ? 'Posting by itself is off. Press Post now when you are ready.'
        : !d.on ? 'Extra posts are off, so this does not go out by itself. Press Post now to send it.'
          : p.due_at ? 'Goes out by itself ' + whenLong(new Date(p.due_at)) + '.' : 'Goes out by itself on the day.';
  } else if (p.status === 'needs_you') hint = p.error || 'Something was flagged, so it will not go out by itself. Fix it, or press Post now.';
  else if (p.status === 'held') hint = p.error || 'Held. It does not go out until you press Post now or Let it post.';
  else if (p.status === 'skipped') hint = p.error || 'Skipped. Nothing goes on ' + whose + ' LinkedIn for this one.';
  else if (p.status === 'failed') hint = p.error || 'It did not go out.';
  else if (p.status === 'writing') hint = 'Being written. About half a minute.';
  else if (p.status === 'posting') hint = 'Sending to LinkedIn now.';
  else if (p.status === 'posted') hint = 'Posted ' + ukTime(p.posted_at) + ' as ' + p.account_name + (p.posted_by && p.posted_by !== 'Automatic' ? ' by ' + p.posted_by : ', by itself') + '.';

  return (
    <>
      <div className={'gtli-state gtli-state--' + st.tone}>
        <span className={'gtjf-pill gtjf-pill--' + st.tone}>{st.label}</span>
        <span className="gtli-state-t">{hint}</span>
        {p.status === 'posted' && p.url && <a className="gt-btn" href={p.url} target="_blank" rel="noopener noreferrer">Open on LinkedIn &#8599;</a>}
      </div>
      {p.status === 'posted' && p.error && <p className="gtli-note gtli-note--amber">{p.error}</p>}

      {p.status === 'writing' ? <div className="gtins-writing gtli-writing"><span className="gtins-dot" />Writing the LinkedIn post…</div> : edit && canPost ? (
        <div className="gtli-edit">
          <textarea className="gtiv-in gtins-ta gtli-ta" rows={14} value={f.text} onChange={(e) => setF(Object.assign({}, f, { text: e.target.value }))} aria-label="Post text" />
          <label className="gtins-field"><span className="gtins-k">Hashtags<em>Up to three, separated by commas</em></span>
            <input className="gtiv-in" value={f.hashtags} onChange={(e) => setF(Object.assign({}, f, { hashtags: e.target.value }))} /></label>
          <span className="gtli-count">{(f.text || '').trim().length.toLocaleString('en-GB')} characters before hashtags · LinkedIn shows about the first 210 before "see more"</span>
        </div>
      ) : <LinkedInExtraPreview p={p} group={group} k={k} authFetch={authFetch} />}

      {(p.flags || []).length > 0 && p.status !== 'posted' && (
        <ul className="gtjf-flags gtins-flags">{p.flags.map((x, i) => <li key={i} className={x.code === 'trainer' ? 'gtjf-flag--stop' : ''}>{x.text}</li>)}</ul>
      )}

      {canPost && (
        <div className="gtli-acts">
          {edit
            ? <><button type="button" className="gt-btn" disabled={!!busy || !dirty} onClick={save}>{busy === 'save' ? 'Saving…' : 'Save'}</button><button type="button" className="gtiv-link" onClick={() => { setEdit(false); setF({ text: p.text, hashtags: pTags }); }}>Cancel</button></>
            : <button type="button" className="gt-btn" disabled={!!busy} onClick={() => setEdit(true)}>Edit</button>}
          <button type="button" className="gt-btn gt-btn--primary" disabled={!!busy || trainer} title={trainer ? 'Remove the trainer or human data wording first' : ''} onClick={postNow}>{busy === 'post' ? 'Posting…' : 'Post now'}</button>
          {p.status === 'held' ? <button type="button" className="gtiv-link" disabled={!!busy} onClick={() => run('release')}>Let it post</button>
            : <button type="button" className="gtiv-link" disabled={!!busy} onClick={() => run('hold')}>Hold</button>}
          <button type="button" className="gtiv-link gtli-skip" disabled={!!busy} onClick={() => run('skip')}>Skip</button>
        </div>
      )}
      {p.status === 'skipped' && <div className="gtli-acts"><button type="button" className="gt-btn" disabled={!!busy} onClick={() => run('release')}>Bring it back</button></div>}
      {canPost && !edit && (
        <div className="gtli-rw">
          <input className="gtiv-in" placeholder="Note for the writer (optional), for example: shorter, open on why it is hard to hire" value={note} onChange={(e) => setNote(e.target.value)} aria-label="Note for the writer" />
          <button type="button" className="gt-btn" disabled={!!busy} onClick={rewrite}>{busy === 'write' ? 'Starting…' : 'Rewrite'}</button>
        </div>
      )}
      {msg && <p className="gtli-note">{msg}</p>}
    </>
  );
}

// The post as it will look in the feed, with the page it links to as the card.
function LinkedInExtraPreview({ p, group, k, authFetch }) {
  const [more, setMore] = useState(false);
  const [pic, setPic] = useState(null); // C1-LIEXTRA7: the real picture that goes to LinkedIn
  useEffect(() => {
    let live = true;
    if (!authFetch) return undefined;
    authFetch('/api/linkedin/extra/admin/post/' + p.id + '/picture').then((x) => { if (live && x && x.src) setPic(x.src); }).catch(() => {});
    return () => { live = false; };
  }, [authFetch, p.id]);
  const tags = (p.hashtags || []).map((t) => '#' + t).join(' ');
  const long = String(p.text || '').length > 260;
  return (
    <div className="gtli-feed">
      <div className="gtli-feed-h">
        <Avatar a={{ name: p.account_name, picture: p.account_picture }} size={42} />
        <span className="gtli-feed-hn"><b>{p.account_name}</b><em>{p.status === 'posted' && p.posted_at ? ukDay(p.posted_at) : lxDay(group.date)} · Public</em></span>
      </div>
      <div className={'gtli-feed-t' + (long && !more ? ' gtli-feed-t--fold' : '')}>
        {String(p.text || '').split(/\n\n+/).map((para, i) => <p key={i}>{para}</p>)}
        {tags && <p className="gtli-feed-tags">{tags}</p>}
      </div>
      {long && <button type="button" className="gtli-feed-more" onClick={() => setMore((m) => !m)}>{more ? 'show less' : '…see more'}</button>}
      {pic ? <img src={pic} alt={group.link_title} width={1200} height={630} style={{ display: 'block', width: '100%', height: 'auto', marginTop: 6 }} /> : (
        <div className="gtli-feed-card">
          <span className="gtli-feed-pill">{k.pill}</span>
          <b>{group.link_title}</b>
          <span className="gtli-feed-by">Generative Talent Co</span>
        </div>
      )}
      <div className="gtli-feed-lnk"><b>{group.link_title}</b><span>{k.site}</span></div>
    </div>
  );
}

// ---------------------------------------------------------------- Insights by email (C1-NEWS)
// Every article goes to the email list when it goes live, written by itself the day before. Anything flagged waits.
const NL_STATE = {
  writing: { label: 'Writing', tone: 'blue' }, draft: { label: 'Ready', tone: 'green' }, needs_you: { label: 'Needs you', tone: 'amber' },
  held: { label: 'Held', tone: 'slate' }, sending: { label: 'Sending', tone: 'blue' }, sent: { label: 'Sent', tone: 'green' },
  failed: { label: 'Did not go out', tone: 'rose' }, skipped: { label: 'Skipped', tone: 'slate' },
};
const NL_STATUS = { subscribed: { label: 'Subscribed', tone: 'green' }, pending: { label: 'Waiting to confirm', tone: 'amber' }, unsubscribed: { label: 'Unsubscribed', tone: 'slate' }, bounced: { label: 'Bounced', tone: 'rose' }, complained: { label: 'Marked as spam', tone: 'rose' } };
const NL_SOURCE = { site: 'Signed up on the site', role_kit: 'Role Kit download', enquiry: 'Search form', beth: 'Beth lead', client_contact: 'Client contact', import: 'Imported', manual: 'Added by hand' };
const nf = (n) => Number(n || 0).toLocaleString('en-GB');
function MailIcon() { return <span className="gtnl-ic" aria-hidden="true"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="M3.5 6.5l8.5 6.5 8.5-6.5" /></svg></span>; }
function domainNote(d) {
  if (!d) return '';
  if (d.status === 'verified') return '';
  if (d.status === 'no_key') return 'Sending is off here: no Resend key on this server.';
  if (d.status === 'not_added') return d.name + ' is not added in Resend yet, so nothing can send.';
  if (d.status === 'unknown') return 'Could not check ' + d.name + ' in Resend just now.';
  return d.name + ' is not verified in Resend yet (' + d.status + '), so nothing can send.';
}

function useNewsSummary(authFetch) {
  const [s, setS] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(() => authFetch('/api/newsletter/summary').then((x) => { setS(x); setErr(''); return x; }).catch((e) => setErr(e.message)), [authFetch]);
  useEffect(() => { load(); }, [load]);
  return [s, setS, load, err];
}

function EmailBar({ authFetch, navigate }) {
  const [s, setS, , err] = useNewsSummary(authFetch);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  if (!s) return err ? <div className="gtiv-card gtli-bar"><span className="gtli-bar-t"><b>Email list</b><span>{err}</span></span></div> : null;
  const c = s.counts || {};
  const note = domainNote(s.domain);
  async function setAuto(on) {
    setBusy(true); setMsg('');
    try { setS(await authFetch('/api/newsletter/settings', { method: 'POST', body: JSON.stringify({ autosend: on }) })); } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  }
  return (
    <section className={'gtiv-card gtli-bar gtnl-bar' + (note ? ' gtli-bar--amber' : '')}>
      <div className="gtli-bar-row">
        <MailIcon />
        <span className="gtli-bar-t"><em>Email</em><b>{nf(c.subscribed)} {c.subscribed === 1 ? 'person gets' : 'people get'} each article by email</b>
          <span>{note || 'Sent when the article goes live, from ' + String(s.from || '').replace(/^.*</, '').replace(/>$/, '') + (s.last ? '. Last: ' + s.last.title + ', ' + ukDay(s.last.sent_at) + ', to ' + nf(s.last.total) + '.' : '.')}</span></span>
        <span className="gtli-bar-a">
          <span className="gtli-seg" role="group" aria-label="Send by itself">
            <button type="button" className={'gtli-seg-b' + (s.autosend ? ' gtli-seg-b--on' : '')} disabled={busy} onClick={() => !s.autosend && setAuto(true)}>Sends by itself</button>
            <button type="button" className={'gtli-seg-b' + (!s.autosend ? ' gtli-seg-b--on' : '')} disabled={busy} onClick={() => s.autosend && setAuto(false)}>I press Send</button>
          </span>
          <button type="button" className="gt-btn" onClick={() => navigate('/insights/list')}>Open the list</button>
        </span>
      </div>
      <div className="gtnl-counts">
        {['pending', 'unsubscribed', 'bounced', 'complained'].map((k) => <span key={k} className="gtnl-count"><i className={'gtli-dot gtli-dot--' + NL_STATUS[k].tone} />{NL_STATUS[k].label} <b>{nf(c[k])}</b></span>)}
      </div>
      {!s.autopilot && <p className="gtli-note">Nothing sends by itself here. It runs on work.gtco.io only. Send now still works and goes to real people on this list.</p>}
      {msg && <p className="gtli-note">{msg}</p>}
    </section>
  );
}

// The email as people will see it, in its own frame so its styles never mix with the app's.
function EmailFrame({ html }) {
  const [h, setH] = useState(640);
  return <iframe className="gtnl-frame" title="Email preview" srcDoc={html} sandbox="allow-same-origin" style={{ height: h }}
    onLoad={(e) => { try { setH(Math.min(1400, e.target.contentDocument.documentElement.scrollHeight + 4)); } catch (x) { /* keep the default */ } }} />;
}

function EmailCard({ authFetch, article }) {
  const [s] = useNewsSummary(authFetch);
  const [x, setX] = useState(undefined);
  const [f, setF] = useState(null);
  const [edit, setEdit] = useState(false);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [note, setNote] = useState('');
  const api = '/api/newsletter/send/' + article.id;
  const fill = (d) => setF(d && d.email ? { subject: d.email.subject, preheader: d.email.preheader, intro: d.email.intro, points: [0, 1, 2].map((i) => (d.email.points || [])[i] || '') } : null);
  const load = useCallback(() => authFetch(api).then((d) => { setX(d); return d; }).catch((e) => setMsg(e.message)), [authFetch, api]);
  useEffect(() => { load(); }, [load]);
  const e = x && x.email;
  const moving = e && (e.status === 'writing' || e.status === 'sending');
  useEffect(() => { if (!moving) return undefined; const t = setInterval(load, 3000); return () => clearInterval(t); }, [moving, load]);
  const eKey = e ? e.status + '|' + e.subject + '|' + e.intro + '|' + (e.points || []).join('|') + '|' + e.preheader : '';
  useEffect(() => { if (!edit && x) fill(x); }, [eKey, edit]); // eslint-disable-line react-hooks/exhaustive-deps

  if (x === undefined) return msg ? <section className="gtiv-card gtins-card gtnl"><span className="gtins-k">Email to subscribers</span><p className="gtiv-err gtjf-msg">{msg}</p></section> : null;
  const live = article.status === 'published';
  const st = e ? NL_STATE[e.status] || { label: e.status, tone: 'slate' } : null;
  const trainer = e && (e.flags || []).some((y) => y.code === 'trainer');
  const canSend = e && ['draft', 'needs_you', 'held', 'failed'].includes(e.status);
  const dirty = e && f && (f.subject !== e.subject || f.preheader !== e.preheader || f.intro !== e.intro || f.points.join('|') !== [0, 1, 2].map((i) => (e.points || [])[i] || '').join('|'));

  async function run(action, body) {
    setBusy(action); setMsg('');
    try {
      const r = await authFetch(api + '/' + action, { method: 'POST', body: JSON.stringify(body || {}) });
      setX(r);
      if (action === 'send') setMsg(r.ok ? 'Sending to ' + nf(r.total) + (r.total === 1 ? ' person' : ' people') + ' now.' : r.message);
      if (action === 'test') setMsg('Test sent to ' + r.to + '.');
      if (action === 'write') { setNote(''); setEdit(false); setTimeout(load, 800); }
    } catch (err) { setMsg(err.message); } finally { setBusy(''); }
  }
  async function save() {
    setBusy('save'); setMsg('');
    try { const r = await authFetch(api, { method: 'PATCH', body: JSON.stringify(Object.assign({}, f, { points: f.points.filter((p) => p.trim()) })) }); setX(r); setEdit(false); setMsg('Saved.'); return true; }
    catch (err) { setMsg(err.message); return false; } finally { setBusy(''); }
  }
  async function sendNow() {
    if (dirty && !(await save())) return;
    const warn = (e.flags || []).filter((y) => y.code !== 'trainer');
    const lines = ['It goes to ' + nf(x.subscribed) + (x.subscribed === 1 ? ' person' : ' people') + ' on the list now. It cannot be called back once it starts.'];
    if (warn.length) lines.push(warn.length + (warn.length === 1 ? ' flag is' : ' flags are') + ' worth a look first: ' + warn.map((y) => y.text).join(' · '));
    if (!(await gtConfirm({ title: 'Send the email?', message: lines, confirmLabel: 'Send it' }))) return;
    run('send');
  }
  async function rewrite() {
    if (e && e.subject && !(await gtConfirm({ title: 'Rewrite the email?', message: 'It is written again from the article. Your edits to the email are replaced.', confirmLabel: 'Rewrite it' }))) return;
    run('write', { note: note.trim() || null });
  }

  let hint = '';
  if (e) {
    if (e.status === 'draft') hint = s && !s.autopilot ? 'Nothing sends by itself here. Press Send now to send it.' : s && !s.autosend ? 'Sending by itself is off. Press Send now when you are ready.' : live ? 'Goes out by itself within a minute.' : 'Goes out by itself when the article goes live.';
    else if (e.status === 'needs_you') hint = 'Something was flagged, so it will not go out by itself. Fix it, or press Send now.';
    else if (e.status === 'held') hint = 'Held. It does not go out until you press Send now or Let it send.';
    else if (e.status === 'skipped') hint = 'Skipped. No email for this article.';
    else if (e.status === 'failed') hint = e.error || 'It did not go out.';
    else if (e.status === 'writing') hint = 'Being written from the article. About half a minute.';
    else if (e.status === 'sending') hint = 'Sending: ' + nf(e.counts.sent) + ' of ' + nf(e.total) + ' so far.' + (e.error ? ' ' + e.error : '');
    else if (e.status === 'sent') hint = 'Sent ' + ukTime(e.sent_at) + ' to ' + nf(e.counts.sent) + (e.counts.sent === 1 ? ' person' : ' people') + (e.sent_by && e.sent_by !== 'Automatic' ? ', by ' + e.sent_by : ', by itself') + '.';
  }
  const c = e && e.counts;
  const rate = (n) => (c && c.sent ? Math.round((n / c.sent) * 100) + '%' : '');

  return (
    <section className="gtiv-card gtins-card gtnl">
      <span className="gtins-k">Email to subscribers<em>{x.subscribed !== undefined ? nf(x.subscribed) + (x.subscribed === 1 ? ' person' : ' people') + ' on the list. Each gets their own unsubscribe link.' : ''}</em></span>
      {!e ? (
        <div className="gtli-empty">
          <span>{live || article.status === 'scheduled' ? 'Not written yet. It is written by itself before the article goes live, or now if you want to read it first.' : 'Written once the article is scheduled. You can write it now to read it first.'}</span>
          <button type="button" className="gt-btn" disabled={!!busy || !String(article.body || '').trim()} onClick={() => run('write')}>{busy === 'write' ? 'Starting…' : 'Write it now'}</button>
        </div>
      ) : (
        <>
          <div className={'gtli-state gtli-state--' + st.tone}>
            <span className={'gtjf-pill gtjf-pill--' + st.tone}>{st.label}</span>
            <span className="gtli-state-t">{hint}</span>
          </div>
          {(e.status === 'sent' || e.status === 'sending') && (
            <div className="gtnl-stats">
              <span><b>{nf(c.sent)}</b>Sent</span>
              <span><b>{nf(c.delivered)}</b>Delivered</span>
              <span><b>{nf(c.clicked)}</b>Clicked<em>{rate(c.clicked)}</em></span>
              <span><b>{nf(c.unsubscribed)}</b>Unsubscribed</span>
              <span className={c.bounced + c.complained ? 'gtnl-stat--warn' : ''}><b>{nf(c.bounced + c.complained)}</b>Bounced or spam</span>
              {c.failed > 0 && <span className="gtnl-stat--warn"><b>{nf(c.failed)}</b>Did not go</span>}
            </div>
          )}
          {e.status === 'writing' ? <div className="gtins-writing gtli-writing"><span className="gtins-dot" />Writing the email…</div> : edit && canSend ? (
            <div className="gtli-edit gtnl-edit">
              <label className="gtins-field"><span className="gtins-k">Subject<em>{f.subject.length} / 70</em></span><input className="gtiv-in" value={f.subject} onChange={(ev) => setF(Object.assign({}, f, { subject: ev.target.value }))} /></label>
              <label className="gtins-field"><span className="gtins-k">Preview line<em>Shown after the subject in the inbox. {f.preheader.length} / 110</em></span><input className="gtiv-in" value={f.preheader} onChange={(ev) => setF(Object.assign({}, f, { preheader: ev.target.value }))} /></label>
              <label className="gtins-field"><span className="gtins-k">Intro<em>Two sentences</em></span><textarea className="gtiv-in gtins-ta" rows={3} value={f.intro} onChange={(ev) => setF(Object.assign({}, f, { intro: ev.target.value }))} /></label>
              <span className="gtins-k">Three points</span>
              {f.points.map((pt, i) => <input key={i} className="gtiv-in" value={pt} aria-label={'Point ' + (i + 1)} onChange={(ev) => setF(Object.assign({}, f, { points: f.points.map((q, n) => (n === i ? ev.target.value : q)) }))} />)}
            </div>
          ) : e.html ? <EmailFrame html={e.html} /> : null}

          {(e.flags || []).length > 0 && !['sent', 'sending'].includes(e.status) && (
            <ul className="gtjf-flags gtins-flags">{e.flags.map((y, i) => <li key={i} className={y.code === 'trainer' ? 'gtjf-flag--stop' : ''}><b>{({ subject: 'Subject', preheader: 'Preview line', intro: 'Intro', points: 'Points' })[y.field] || y.field}</b> {y.text}</li>)}</ul>
          )}

          {canSend && (
            <div className="gtli-acts">
              {edit
                ? <><button type="button" className="gt-btn" disabled={!!busy || !dirty} onClick={save}>{busy === 'save' ? 'Saving…' : 'Save'}</button><button type="button" className="gtiv-link" onClick={() => { setEdit(false); fill(x); }}>Cancel</button></>
                : <button type="button" className="gt-btn" disabled={!!busy} onClick={() => setEdit(true)}>Edit</button>}
              <button type="button" className="gt-btn gt-btn--primary" disabled={!!busy || !live || trainer || !x.subscribed} title={!live ? 'The article is not live yet' : trainer ? 'Remove the trainer or human data wording first' : !x.subscribed ? 'Nobody is on the list yet' : ''} onClick={sendNow}>{busy === 'send' ? 'Starting…' : 'Send now'}</button>
              <button type="button" className="gtiv-link" disabled={!!busy} onClick={() => run('test')}>{busy === 'test' ? 'Sending…' : 'Send me a test'}</button>
              {e.status === 'held' ? <button type="button" className="gtiv-link" disabled={!!busy} onClick={() => run('release')}>Let it send</button>
                : <button type="button" className="gtiv-link" disabled={!!busy} onClick={() => run('hold')}>Hold</button>}
              <button type="button" className="gtiv-link gtli-skip" disabled={!!busy} onClick={() => run('skip')}>Skip</button>
            </div>
          )}
          {e.status === 'skipped' && <div className="gtli-acts"><button type="button" className="gt-btn" disabled={!!busy} onClick={() => run('release')}>Bring it back</button></div>}
          {canSend && !edit && (
            <div className="gtli-rw">
              <input className="gtiv-in" placeholder="Note for the writer (optional), for example: lead with the pay finding" value={note} onChange={(ev) => setNote(ev.target.value)} aria-label="Note for the writer" />
              <button type="button" className="gt-btn" disabled={!!busy} onClick={rewrite}>{busy === 'write' ? 'Starting…' : 'Rewrite'}</button>
            </div>
          )}
        </>
      )}
      {msg && <p className="gtli-note">{msg}</p>}
    </section>
  );
}

// ---------------------------------------------------------------- the list itself: /insights/list
function EmailList({ authFetch, navigate }) {
  const [s, , reloadSummary] = useNewsSummary(authFetch);
  const [status, setStatus] = useState('subscribed');
  const [source, setSource] = useState('');
  const [q, setQ] = useState('');
  const [dq, setDq] = useState('');
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [next, setNext] = useState(null);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [adding, setAdding] = useState(false);
  const [af, setAf] = useState({ email: '', name: '', company: '' });
  const [imp, setImp] = useState(null);
  useEffect(() => { const t = setTimeout(() => setDq(q.trim()), 250); return () => clearTimeout(t); }, [q]);
  const query = useCallback((before) => authFetch('/api/newsletter/contacts?' + new URLSearchParams(Object.assign({ status, source, q: dq }, before ? { before } : {})).toString()), [authFetch, status, source, dq]);
  const load = useCallback(() => query(null).then((r) => { setRows(r.contacts); setTotal(r.total); setNext(r.next); }).catch((e) => setMsg(e.message)), [query]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); reloadSummary(); };
  async function more() { setBusy('more'); try { const r = await query(next); setRows((x) => x.concat(r.contacts)); setNext(r.next); } catch (e) { setMsg(e.message); } finally { setBusy(''); } }

  async function addOne() {
    setBusy('add'); setMsg('');
    try {
      const r = await authFetch('/api/newsletter/contacts', { method: 'POST', body: JSON.stringify(af) });
      const o = r.outcome;
      setMsg(o === 'added' ? af.email + ' is on the list.' : o === 'already' ? af.email + ' was already on the list.' : o && o.indexOf('left_') === 0 ? af.email + ' ' + (o === 'left_unsubscribed' ? 'unsubscribed before' : o === 'left_bounced' ? 'bounced before' : 'marked an email as spam') + ', so they were not added. Find them in the list to put them back.' : 'Not added.');
      if (o === 'added') { setAf({ email: '', name: '', company: '' }); setAdding(false); }
      refresh();
    } catch (e) { setMsg(e.message); } finally { setBusy(''); }
  }
  async function act(c, action) {
    if (action === 'resubscribe' && !(await gtConfirm({ title: 'Put ' + (c.first_name || c.email) + ' back on the list?', message: [c.status === 'unsubscribed' ? 'They unsubscribed' + (c.unsubscribed_at ? ' on ' + ukDay(c.unsubscribed_at) : '') + '.' : c.status === 'complained' ? 'They marked an Insights email as spam.' : 'An email to them bounced.', 'Only do this if they asked to come back. Emailing people who left can hurt the domain.'], confirmLabel: 'Put them back', tone: 'danger' }))) return;
    if (action === 'remove' && !(await gtConfirm({ title: 'Remove ' + c.email + '?', message: 'They come off the list and their history here is deleted. If you only want to stop emailing them, unsubscribe them instead: that keeps them from being added again by an import.', confirmLabel: 'Remove', tone: 'danger' }))) return;
    setBusy(action + c.id); setMsg('');
    try { await authFetch('/api/newsletter/contacts/' + c.id + '/' + action, { method: 'POST', body: '{}' }); refresh(); } catch (e) { setMsg(e.message); } finally { setBusy(''); }
  }
  const counts = (s && s.counts) || {};

  return (
    <div className="gtc-wrap gtins gtnl-list">
      <button type="button" className="gtjf-back" onClick={() => navigate('/insights/email')}>&larr; Email</button>{/* C1-INSTABS */}
      <div className="gtc-head">
        <div>
          <h1 className="gtc-title">Insights email list</h1>
          <p className="gtc-sub">Everyone who gets each Insights article by email. People who unsubscribe, bounce or mark an email as spam are never added back by an import, Beth or the CRM.</p>
        </div>
        <div className="gtjf-actions">
          <button type="button" className="gt-btn" onClick={() => { setImp(imp ? null : { step: 'pick' }); setAdding(false); }}>Import a CSV</button>
          <button type="button" className="gt-btn gt-btn--primary" onClick={() => { setAdding((a) => !a); setImp(null); }}>Add someone</button>
        </div>
      </div>
      {s && domainNote(s.domain) && <div className="gtiv-card gtjf-banner gtnl-warn">{domainNote(s.domain)}</div>}

      {adding && (
        <section className="gtiv-card gtnl-add">
          <input className="gtiv-in" placeholder="Work email" value={af.email} onChange={(e) => setAf(Object.assign({}, af, { email: e.target.value }))} aria-label="Email" />
          <input className="gtiv-in" placeholder="Name (optional)" value={af.name} onChange={(e) => setAf(Object.assign({}, af, { name: e.target.value }))} aria-label="Name" />
          <input className="gtiv-in" placeholder="Company (optional)" value={af.company} onChange={(e) => setAf(Object.assign({}, af, { company: e.target.value }))} aria-label="Company" />
          <button type="button" className="gt-btn gt-btn--primary" disabled={!!busy || !af.email.trim()} onClick={addOne}>{busy === 'add' ? 'Adding…' : 'Add'}</button>
        </section>
      )}
      {imp && <EmailImport authFetch={authFetch} onDone={(m) => { setImp(null); setMsg(m); refresh(); }} onCancel={() => setImp(null)} />}

      <div className="gtnl-tiles" role="tablist" aria-label="Status">
        {['subscribed', 'pending', 'unsubscribed', 'bounced', 'complained'].map((k) => (
          <button key={k} type="button" role="tab" aria-selected={status === k} className={'gtnl-tile' + (status === k ? ' gtnl-tile--on' : '')} onClick={() => setStatus(k)}>
            <b>{nf(counts[k])}</b><span><i className={'gtli-dot gtli-dot--' + NL_STATUS[k].tone} />{NL_STATUS[k].label}</span>
          </button>
        ))}
      </div>
      <div className="gtjf-bar2">
        <div className="gtivl-filters" role="tablist" aria-label="Where they came from">
          <button type="button" className={'gtivl-filter' + (!source ? ' gtivl-filter--on' : '')} onClick={() => setSource('')}>Every source</button>
          {Object.keys(NL_SOURCE).map((k) => <button key={k} type="button" className={'gtivl-filter' + (source === k ? ' gtivl-filter--on' : '')} onClick={() => setSource(k)}>{NL_SOURCE[k]}</button>)}
        </div>
        <input className="gtiv-in gtjf-search" type="search" placeholder="Search name, email or company" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search the list" />
      </div>
      {msg && <div className="gtiv-card gtjf-banner">{msg}</div>}
      {!rows ? <div className="gtiv-card gtiv-loading">Loading the list…</div> : rows.length === 0 ? (
        <div className="gtiv-card gtivl-empty"><b>{dq || source ? 'Nobody matches' : status === 'subscribed' ? 'Nobody on the list yet' : 'Nobody here'}</b><span>{status === 'subscribed' && !dq && !source ? 'People join from the subscribe box on Insights, the tick boxes on the Role Kit and search form, Beth, client contacts and imports.' : 'Try another filter.'}</span></div>
      ) : (
        <>
          <p className="gtnl-n">{nf(total)} {total === 1 ? 'person' : 'people'}</p>
          <div className="gtivl-list">
            {rows.map((c) => {
              const name = [c.first_name, c.last_name].filter(Boolean).join(' ');
              const stt = NL_STATUS[c.status] || { label: c.status, tone: 'slate' };
              return (
                <div key={c.id} className="gtiv-card gtivl-row gtnl-row">
                  <span className="gtnl-av" aria-hidden="true">{initials(name || c.email)}</span>
                  <span className="gtivl-row-t">
                    <b>{name || c.email}</b>
                    <span>{name ? c.email : ''}{name && (c.company || c.client_name) ? ' · ' : ''}{c.client_id ? <a className="gtiv-link" href={'/clients/' + c.client_id} onClick={(e) => { e.preventDefault(); navigate('/clients/' + c.client_id); }}>{c.client_name || c.company}</a> : c.company}</span>
                    <span>{(c.source === 'import' || c.source === 'manual') && c.source_detail ? c.source_detail : (NL_SOURCE[c.source] || c.source) + (c.source_detail ? ', ' + c.source_detail.replace(/^Added by /, 'added by ') : '')} · added {ukDay(c.created_at)}{c.sends ? ' · ' + c.sends + (c.sends === 1 ? ' email' : ' emails') + (c.clicks ? ', ' + c.clicks + (c.clicks === 1 ? ' click' : ' clicks') : '') : ''}{c.unsubscribed_at && c.status !== 'subscribed' ? ' · left ' + ukDay(c.unsubscribed_at) : ''}</span>
                  </span>
                  <span className={'gtjf-pill gtjf-pill--' + stt.tone}>{stt.label}</span>
                  <span className="gtnl-row-a">
                    {(c.status === 'subscribed' || c.status === 'pending') && <button type="button" className="gtiv-link" disabled={!!busy} onClick={() => act(c, 'unsubscribe')}>Unsubscribe</button>}
                    {['unsubscribed', 'bounced', 'complained'].includes(c.status) && <button type="button" className="gtiv-link" disabled={!!busy} onClick={() => act(c, 'resubscribe')}>Put back</button>}
                    <button type="button" className="gtiv-link gtli-skip" disabled={!!busy} onClick={() => act(c, 'remove')}>Remove</button>
                  </span>
                </div>
              );
            })}
          </div>
          {next && <button type="button" className="gt-btn gtnl-more" disabled={!!busy} onClick={more}>{busy === 'more' ? 'Loading…' : 'Show 100 more'}</button>}
        </>
      )}
    </div>
  );
}

// CSV import: choose a file, check the columns and what will happen, then add. Warns when a file looks bought; never blocks.
function EmailImport({ authFetch, onDone, onCancel }) {
  const [file, setFile] = useState(null);
  const [pv, setPv] = useState(null);
  const [mapping, setMapping] = useState(null);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  async function preview(csv, name, m) {
    setBusy('check'); setMsg('');
    try { const r = await authFetch('/api/newsletter/import/preview', { method: 'POST', body: JSON.stringify({ csv, file_name: name, mapping: m || null }) }); setPv(r); setMapping(r.mapping); }
    catch (e) { setMsg(e.message); } finally { setBusy(''); }
  }
  function pick(ev) {
    const fl = ev.target.files && ev.target.files[0];
    if (!fl) return;
    if (fl.size > 5 * 1024 * 1024) { setMsg('That file is over 5 MB. Split it and import the parts.'); return; }
    const rd = new FileReader();
    rd.onload = () => { const csv = String(rd.result || ''); setFile({ name: fl.name, csv }); preview(csv, fl.name, null); };
    rd.readAsText(fl);
  }
  function remap(k, v) { const m = Object.assign({}, mapping, { [k]: v === '' ? null : Number(v) }); setMapping(m); preview(file.csv, file.name, m); }
  async function commit() {
    setBusy('add'); setMsg('');
    try {
      const r = await authFetch('/api/newsletter/import/commit', { method: 'POST', body: JSON.stringify({ csv: file.csv, file_name: file.name, mapping }) });
      onDone(nf(r.added) + ' added from ' + file.name + '. ' + nf(r.already) + ' already on the list' + (r.left_out ? ', ' + nf(r.left_out) + ' left out because they unsubscribed, bounced or marked an email as spam' : '') + (r.invalid ? ', ' + nf(r.invalid) + ' rows had no usable email' : '') + '.');
    } catch (e) { setMsg(e.message); setBusy(''); }
  }
  const opts = pv ? [{ value: '', label: 'Not in this file' }].concat(pv.headers.map((h, i) => ({ value: String(i), label: h || 'Column ' + (i + 1) }))) : [];
  const FIELDS = [['email', 'Email'], ['first_name', 'First name'], ['last_name', 'Last name'], ['name', 'Full name'], ['company', 'Company']];
  return (
    <section className="gtiv-card gtnl-imp">
      <div className="gtnl-imp-h"><b>Import a CSV</b><span>One person per row with an email column. Apollo, Sales Navigator, Clay and most exports work as they are.</span></div>
      {!pv ? (
        <label className="gt-btn gtnl-file"><input type="file" accept=".csv,text/csv" onChange={pick} />{busy === 'check' ? 'Reading…' : 'Choose a file'}</label>
      ) : (
        <>
          <div className="gtnl-map">
            {FIELDS.map(([k, l]) => (
              <label key={k} className="gtins-field"><span className="gtins-k">{l}</span>
                <GtSelect value={mapping[k] === null || mapping[k] === undefined ? '' : String(mapping[k])} options={opts} onChange={(v) => remap(k, v)} width={200} /></label>
            ))}
          </div>
          <div className="gtnl-stats gtnl-stats--imp">
            <span><b>{nf(pv.outcome.new)}</b>New to the list</span>
            <span><b>{nf(pv.outcome.already)}</b>Already on it</span>
            <span className={pv.outcome.left_out ? 'gtnl-stat--warn' : ''}><b>{nf(pv.outcome.left_out)}</b>Left out: they left before</span>
            <span><b>{nf(pv.outcome.no_email + pv.outcome.duplicates)}</b>No email or repeated</span>
          </div>
          {pv.warnings.length > 0 && <ul className="gtjf-flags gtins-flags gtnl-warns">{pv.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>}
          <div className="gtnl-sample">{pv.sample.filter((p) => p.email).slice(0, 4).map((p, i) => <span key={i}><b>{p.name || [p.first_name, p.last_name].filter(Boolean).join(' ') || p.email}</b>{p.email}{p.company ? ' · ' + p.company : ''}</span>)}</div>
          <div className="gtli-acts">
            <button type="button" className="gt-btn gt-btn--primary" disabled={!!busy || !pv.outcome.new || mapping.email === null} onClick={commit}>{busy === 'add' ? 'Adding…' : 'Add ' + nf(pv.outcome.new) + (pv.outcome.new === 1 ? ' person' : ' people')}</button>
            <button type="button" className="gtiv-link" onClick={onCancel}>Cancel</button>
            <span className="gtnl-file-n">{file.name} · {nf(pv.rows)} rows</span>
          </div>
        </>
      )}
      {msg && <p className="gtli-note">{msg}</p>}
    </section>
  );
}

function PostList({ authFetch, navigate, tab }) { // C1-INSTABS: Articles, LinkedIn and Email tabs
  const t = tab || 'articles';
  const [attn, setAttn] = useState(null);
  useEffect(() => { authFetch('/api/linkedin/extra/admin/attention').then(setAttn).catch(() => {}); }, [authFetch, t]);
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [series, setSeries] = useState('weekly');
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const load = useCallback(() => authFetch('/api/insights/admin').then((x) => { setD(x); setError(''); }).catch((e) => setError(e.message)), [authFetch]); // C1-INSIGHTS12
  useEffect(() => { load(); }, [load]);

  const posts = useMemo(() => {
    if (!d) return [];
    const words = q.trim().toLowerCase();
    return d.posts.filter((p) => p.series === series && (filter === 'all' || p.status === filter) &&
      (!words || [p.title, p.role && p.role.title, p.role && p.role.family, p.period].filter(Boolean).some((t) => String(t).toLowerCase().indexOf(words) >= 0)));
  }, [d, series, filter, q]);
  const count = (s, st) => (d ? d.posts.filter((p) => p.series === s && (!st || p.status === st)).length : 0);

  return (
    <div className="gtc-wrap gtins">
      <div className="gtc-head">
        <div>
          <h1 className="gtc-title">Insights</h1>
          <p className="gtc-sub">{t === 'linkedin' ? 'Every LinkedIn post: each article, two extra posts a week and anything extra. Coming up shows the next two weeks; everything else is in History.' : t === 'email' ? 'Each article goes to the email list when it goes live. Every send is listed here.' : 'Articles on generativetalent.co/insights, written and published for you. Each one goes live the morning after it is written, unless you hold it.'}</p>
        </div>
        <div className="gtjf-actions">
          {d && <a className="gt-btn" href={d.site + '/insights'} target="_blank" rel="noopener noreferrer">Open Insights &#8599;</a>}
        </div>
      </div>
      <InsightsTabs tab={t} navigate={navigate} d={d} attn={attn} />
      {error && <div className="gtiv-card gtiv-err">{error} <button type="button" className="gtiv-link" onClick={load}>Try again</button></div>}
      {t === 'linkedin' && <><LinkedInBar authFetch={authFetch} /><LinkedInExtra authFetch={authFetch} navigate={navigate} />{/* C1-LIEXTRA */}</>}
      {t === 'email' && <><EmailBar authFetch={authFetch} navigate={navigate} />{/* C1-NEWS */}<EmailSends authFetch={authFetch} navigate={navigate} /></>}
      {t === 'articles' && <>

      <div className="gtins-series" role="tablist" aria-label="Series">
        {[['weekly', 'The Agent Workforce', 'One role a week'], ['index', 'AI Workforce Index', 'Every month']].map(([k, l, s]) => (
          <button key={k} type="button" role="tab" aria-selected={series === k} className={'gtins-sw' + (series === k ? ' gtins-sw--on' : '')} onClick={() => setSeries(k)}>
            <b>{l}</b><em>{s} · {count(k, 'published')} live · {count(k, 'draft')} {count(k, 'draft') === 1 ? 'draft' : 'drafts'}</em>
          </button>
        ))}
      </div>

      {d && <Autopilot key={series} authFetch={authFetch} navigate={navigate} series={series} posts={d.posts} reload={load} showDrafts={() => setFilter('draft')} on={d.autopilot !== false} />}
      {series === 'index' && <DemandSweep authFetch={authFetch} navigate={navigate} posts={d ? d.posts : []} />}

      <h2 className="gtins-all">All {series === 'weekly' ? 'articles' : 'issues'}</h2>
      <div className="gtjf-bar2">
        <div className="gtivl-filters" role="tablist">
          {FILTERS.map(([k, l]) => <button key={k} type="button" role="tab" aria-selected={filter === k} className={'gtivl-filter' + (filter === k ? ' gtivl-filter--on' : '')} onClick={() => setFilter(k)}>{l}{k !== 'all' && count(series, k) ? ' ' + count(series, k) : ''}</button>)}
        </div>
        <input className="gtiv-in gtjf-search" type="search" placeholder="Search titles and roles" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search titles and roles" />
      </div>

      {!d ? <div className="gtiv-card gtiv-loading">Loading Insights…</div> : posts.length === 0 ? (
        <div className="gtiv-card gtivl-empty">
          <b>{d.posts.some((p) => p.series === series) ? 'Nothing matches' : series === 'weekly' ? 'No articles yet' : 'No Index issues yet'}</b>
          <span>{d.posts.some((p) => p.series === series) ? 'Try another filter.' : series === 'weekly' ? 'The first one is written on Monday at 06:00.' : 'The first issue is written on the 2nd of the month.'}</span>
        </div>
      ) : (
        <div className="gtivl-list">
          {posts.map((p) => (
            <button key={p.id} type="button" className="gtiv-card gtivl-row gtins-row" onClick={() => navigate('/insights/' + p.id)}>
              <span className="gtivl-row-t">
                <b>{p.title}</b>
                <span>{p.series === 'weekly' ? (p.role ? p.role.title + (p.role.family ? ' · ' + p.role.family : '') : 'No role') : monthLabel(p.period)} · {p.words} words{p.flags.length ? ' · ' + p.flags.length + (p.flags.length === 1 ? ' flag' : ' flags') : ''}</span>
                <span>{p.status === 'published' ? 'Live since ' + ukDay(p.published_at) + ' · ' + p.views + (p.views === 1 ? ' view' : ' views') : p.status === 'scheduled' ? 'Goes live ' + whenLong(p.publish_at) + (p.edited_by === 'weekly schedule' || p.edited_by === 'monthly schedule' ? ', by itself' : '') : p.status === 'writing' ? 'Being written now' : p.edited_by ? 'Last edited by ' + p.edited_by : ''}</span>
              </span>
              <Pill s={p.status} />
            </button>
          ))}
        </div>
      )}
      </>}
    </div>
  );
}

// ---------------------------------------------------------------- one article
function PostEditor({ id, authFetch, navigate }) {
  const [p, setP] = useState(null);
  const [site, setSite] = useState('https://generativetalent.co');
  const [f, setF] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [day, setDay] = useState(null);
  const [hour, setHour] = useState('09:00');
  const [note, setNote] = useState('');
  const load = useCallback(() => authFetch('/api/insights/admin/' + id).then((x) => {
    setP(x);
    setF({ title: x.title, standfirst: x.standfirst || '', body: x.body || '', jimmy_note: x.jimmy_note || '', slug: x.slug, sources: (x.sources || []).length ? x.sources : [] });
    setDirty(false); setError('');
    return x;
  }).catch((e) => setError(e.message)), [authFetch, id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { authFetch('/api/insights/admin').then((x) => setSite(x.site)).catch(() => {}); }, [authFetch]);
  const writing = p && p.status === 'writing';
  useEffect(() => { if (!writing) return undefined; const t = setInterval(load, 4000); return () => clearInterval(t); }, [writing, load]);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  if (error && !p) return <div className="gtc-wrap gtins"><div className="gtiv-card gtiv-err">{error}</div></div>;
  if (!p || !f) return <div className="gtc-wrap gtins"><div className="gtiv-card gtiv-loading">Loading…</div></div>;

  const set = (k, v) => { setF((o) => Object.assign({}, o, { [k]: v })); setDirty(true); };
  const setSource = (i, k, v) => set('sources', f.sources.map((s, n) => (n === i ? Object.assign({}, s, { [k]: v }) : s)));
  const flags = p.flags || [];
  const live = p.status === 'published';
  const trainer = flags.some((x) => x.code === 'trainer');

  async function save(quiet) {
    setBusy('save'); if (!quiet) setMsg('');
    try {
      const body = Object.assign({}, f, { sources: f.sources.filter((s) => s.url) });
      if (p.published_at) delete body.slug;
      const r = await authFetch('/api/insights/admin/' + p.id, { method: 'PATCH', body: JSON.stringify(body) });
      await load();
      if (!quiet) setMsg(r.taken_offline ? 'Saved, and taken offline: it now has trainer or human data wording.' : 'Saved.');
      return true;
    } catch (e) { setMsg(e.message); return false; } finally { setBusy(''); }
  }
  async function publish(at) {
    if (dirty && !(await save(true))) return;
    const warn = (p.flags || []).filter((x) => x.code !== 'trainer');
    if (warn.length && !(await gtConfirm({ title: at ? 'Schedule this article?' : 'Publish this article?', message: [warn.length + (warn.length === 1 ? ' flag is' : ' flags are') + ' worth a look first:', warn.slice(0, 6).map((x) => x.text).join(' · ')], confirmLabel: at ? 'Schedule it' : 'Publish it' }))) return;
    setBusy('pub'); setMsg('');
    try {
      const r = await authFetch('/api/insights/admin/' + p.id + '/publish', { method: 'POST', body: JSON.stringify(at ? { published: true, at } : { published: true }) });
      setMsg(r.status === 'scheduled' ? 'Scheduled for ' + ukTime(r.publish_at) + '. It goes live by itself.' : 'Live. The site shows it within five minutes.');
      await load();
    } catch (e) { setMsg(e.message); } finally { setBusy(''); }
  }
  async function unpublish() {
    const sch = p.status === 'scheduled';
    if (!(await gtConfirm(sch ? { title: 'Hold this article?', message: 'It stays here as a draft and does not go live until you publish it.', confirmLabel: 'Hold it' } : { title: 'Take this article offline?', message: 'It comes off the site within five minutes. The words are kept and you can put it back.', confirmLabel: 'Take it offline', tone: 'danger' }))) return;
    setBusy('pub'); setMsg('');
    try { await authFetch('/api/insights/admin/' + p.id + '/publish', { method: 'POST', body: JSON.stringify({ published: false }) }); setMsg(sch ? 'Held. It stays a draft until you publish it.' : 'Taken offline.'); await load(); } catch (e) { setMsg(e.message); } finally { setBusy(''); }
  }
  // C1-INSIGHTS6: opens the article on the site exactly as it will look. Saves first.
  async function preview() {
    const w = window.open('', '_blank');
    if (dirty && !(await save(true))) { if (w) w.close(); return; }
    if (w) w.location.href = p.preview_url; else setMsg('Your browser blocked the new tab. Allow pop-ups for this site, or open ' + p.preview_url);
  }
  async function rewrite() {
    if ((dirty || stripped(f.body)) && !(await gtConfirm({ title: p.body ? 'Rewrite this article?' : 'Write this article?', message: p.body ? 'The writer starts again from the role and fresh sources. The title, standfirst, article, Our view and sources are all replaced.' : 'The writer drafts it from the role and fresh sources.', confirmLabel: p.body ? 'Rewrite it' : 'Write it' }))) return;
    setBusy('write'); setMsg('');
    try { await authFetch('/api/insights/admin/' + p.id + '/write', { method: 'POST', body: JSON.stringify({ note: note.trim() || null }) }); setNote(''); setDirty(false); await load(); } catch (e) { setMsg(e.message); } finally { setBusy(''); }
  }
  async function remove() {
    if (!(await gtConfirm({ title: 'Delete this draft?', message: 'It has never been published. It is deleted for good.', confirmLabel: 'Delete it', tone: 'danger' }))) return;
    try { await authFetch('/api/insights/admin/' + p.id, { method: 'DELETE' }); navigate('/insights'); } catch (e) { setMsg(e.message); }
  }
  async function back() {
    if (dirty && !(await gtConfirm({ title: 'Leave without saving?', message: 'Your changes to this article will be lost.', confirmLabel: 'Leave', tone: 'danger' }))) return;
    navigate('/insights');
  }
  const scheduleAt = day ? new Date(day + 'T' + hour + ':00') : null;
  function stripped(h) { return String(h || '').replace(/<[^>]*>/g, '').trim(); }

  return (
    <div className="gtc-wrap gtins">
      <button type="button" className="gtjf-back" onClick={back}>&larr; Insights</button>
      <div className="gtc-head">
        <div>
          <h1 className="gtc-title gtins-title">{p.series === 'weekly' ? 'The Agent Workforce' : 'AI Workforce Index'} <Pill s={p.status} /></h1>
          <p className="gtc-sub">{p.series === 'weekly' ? (p.role ? p.role.title + (p.role.family ? ' · ' + p.role.family : '') : 'No role') : monthLabel(p.period)} · {p.words} words · about {Math.max(1, Math.round(p.words / 225))} min read{p.views ? ' · ' + p.views + ' views' : ''}</p>
        </div>
        <div className="gtjf-actions">
          {live && <a className="gt-btn" href={site + '/insights/' + p.slug} target="_blank" rel="noopener noreferrer">View on the site &#8599;</a>}
          <button type="button" className="gt-btn" disabled={!!busy || writing || !stripped(p.body)} onClick={preview}>Preview &#8599;</button>
          <button type="button" className="gt-btn" disabled={!dirty || !!busy || writing} onClick={() => save(false)}>{busy === 'save' ? 'Saving…' : 'Save'}</button>
          {p.status === 'scheduled' ? null : live
            ? <button type="button" className="gt-btn" disabled={!!busy} onClick={unpublish}>Take offline</button>
            : <button type="button" className="gt-btn gt-btn--primary" disabled={!!busy || trainer || writing || !stripped(p.body)} title={trainer ? 'Remove the trainer or human data wording first' : ''} onClick={() => publish(null)}>Publish now</button>}
        </div>
      </div>
      {p.status === 'scheduled' && (
        <div className="gtins-golive">
          <Tile tone="green" top="Goes live" big={dayTile(p.publish_at).wd + ' ' + dayTile(p.publish_at).day + ' ' + dayTile(p.publish_at).mon} low={dayTile(p.publish_at).time} />
          <span className="gtins-golive-t"><b>Goes live {whenLong(p.publish_at)}, by itself.</b><span>Edits you save go out with it. Hold it if you want to keep it back.</span></span>
          <span className="gtins-ar-a">
            <button type="button" className="gt-btn" disabled={!!busy} onClick={unpublish}>Hold</button>
            <button type="button" className="gt-btn gt-btn--primary" disabled={!!busy || trainer || writing} onClick={() => publish(null)}>Publish now</button>
          </span>
        </div>
      )}
      {p.status === 'draft' && !writing && stripped(p.body) && (
        <div className={'gtins-golive gtins-golive--' + (flags.length ? 'amber' : 'slate')}>
          {flags.length ? <Tile tone="amber" top="Waiting" big="Needs you" low={flags.length + (flags.length === 1 ? ' check' : ' checks')} /> : <Tile tone="slate" top="Waiting" big="Held" low="Your call" />}
          <span className="gtins-golive-t"><b>{flags.length ? 'Something was flagged, so this will not go live by itself.' : 'This will not go live until you publish it.'}</b>
            <span>{flags.length ? 'The checks are on the right. Fix them or publish as it is.' : 'Read it, then publish now or pick a time on the right.'}</span></span>
        </div>
      )}
      {msg && <div className="gtiv-card gtjf-banner">{msg}</div>}
      {writing && <div className="gtiv-card gtjf-banner gtjf-banner--blue">Writing the article from the role and fresh sources. It takes about a minute, and this page fills in by itself.</div>}
      {p.status === 'failed' && <div className="gtiv-card gtiv-err">Writing failed: {p.write_error}. Use Rewrite on the right to try again.</div>}

      <div className="gtins-grid">
        <div className="gtins-main">
          <section className="gtiv-card gtins-card">
            <label className="gtins-field"><span className="gtins-k">Title<em>{f.title.length} / 90</em></span>
              <input className="gtiv-in gtins-titlein" value={f.title} onChange={(e) => set('title', e.target.value)} /></label>
            <label className="gtins-field"><span className="gtins-k">Standfirst<em>The line under the title, and what LinkedIn and Google show. {f.standfirst.length} / 200</em></span>
              <textarea className="gtiv-in gtins-ta" rows={2} value={f.standfirst} onChange={(e) => set('standfirst', e.target.value)} /></label>
          </section>
          <section className="gtiv-card gtins-card">
            <span className="gtins-k">The article<em>Headings, lists, quotes and links come through. Tables and colours are taken out on save.</em></span>
            {writing ? <div className="gtins-writing"><span className="gtins-dot" />Writing…</div>
              : <div className="gtins-editor"><GtEditor value={f.body} onChange={(v) => set('body', v)} minHeight={620} placeholder="The writer's draft appears here." /></div>}
          </section>
          <section className="gtiv-card gtins-card gtins-jimmy">
            <span className="gtins-k">Our view<em>Written by the writer in the firm's voice, unsigned. Edit it if you want to; empty hides the box.</em></span>
            <textarea className="gtiv-in gtins-ta" rows={5} value={f.jimmy_note} onChange={(e) => set('jimmy_note', e.target.value)} disabled={writing} placeholder="The writer adds this with the draft." />
          </section>
          <section className="gtiv-card gtins-card">
            <span className="gtins-k">Sources<em>Found and added by the writer when it drafts the article. Remove any you do not want.</em></span>
            <div className="gtins-sources">
              {f.sources.map((s, i) => (
                <div key={i} className="gtins-src">
                  <input className="gtiv-in" placeholder="Title" value={s.title || ''} onChange={(e) => setSource(i, 'title', e.target.value)} />
                  <input className="gtiv-in" placeholder="https://" value={s.url || ''} onChange={(e) => setSource(i, 'url', e.target.value)} />
                  <input className="gtiv-in" placeholder="Publisher" value={s.publisher || ''} onChange={(e) => setSource(i, 'publisher', e.target.value)} />
                  <button type="button" className="gtiv-link" onClick={() => set('sources', f.sources.filter((_, n) => n !== i))}>Remove</button>
                </div>
              ))}
              {f.sources.length === 0 && <p className="gtivl-muted">None yet. The writer adds them with its draft.</p>}
            </div>
          </section>
          {!writing && stripped(p.body) && <LinkedInPost key={p.status} authFetch={authFetch} article={p} />}
          {!writing && stripped(p.body) && <EmailCard key={'e' + p.status} authFetch={authFetch} article={p} />}{/* C1-NEWS */}
        </div>

        <aside className="gtins-side">
          <section className="gtiv-card gtins-card">
            <span className="gtins-k">Worth a look</span>
            {flags.length === 0 ? <p className="gtivl-muted">Nothing flagged.</p> : (
              <ul className="gtjf-flags gtins-flags">{flags.map((x, i) => <li key={i} className={x.code === 'trainer' ? 'gtjf-flag--stop' : ''}><b>{FIELD[x.field] || x.field}</b> {x.text}</li>)}</ul>
            )}
            {dirty && <p className="gtins-note">Flags update when you save.</p>}
          </section>
          {p.series === 'weekly' && !live && p.status !== 'scheduled' && (
            <section className="gtiv-card gtins-card">
              <span className="gtins-k">{p.body ? 'Rewrite' : 'Write'}<em>Starts again from the role and fresh sources.</em></span>
              <input className="gtiv-in" placeholder="Note for the writer (optional), for example: more on security" value={note} onChange={(e) => setNote(e.target.value)} disabled={writing} />
              <button type="button" className="gt-btn" disabled={!!busy || writing} onClick={rewrite}>{busy === 'write' ? 'Starting…' : p.body ? 'Rewrite it' : 'Write it'}</button>
            </section>
          )}
          {!live && p.status !== 'scheduled' && (
            <section className="gtiv-card gtins-card">
              <span className="gtins-k">Schedule<em>London time. It goes live by itself.</em></span>
              <div className="gtins-sched">
                <GtDatePicker value={day} onChange={setDay} placeholder="Pick a day" />
                <GtSelect value={hour} options={HOURS} onChange={setHour} width={110} />
              </div>
              <button type="button" className="gt-btn" disabled={!!busy || !scheduleAt || trainer || scheduleAt <= new Date()} onClick={() => publish(scheduleAt.toISOString())}>Schedule it</button>
            </section>
          )}
          <section className="gtiv-card gtins-card">
            <span className="gtins-k">Address</span>
            {p.published_at
              ? <p className="gtins-slug">generativetalent.co/insights/<b>{p.slug}</b></p>
              : <label className="gtins-field"><span className="gtins-slugpre">generativetalent.co/insights/</span><input className="gtiv-in" value={f.slug} onChange={(e) => set('slug', e.target.value)} /></label>}
            {p.published_at && <p className="gtins-note">Fixed once published, so links keep working.</p>}
          </section>
          {p.role && (
            <section className="gtiv-card gtins-card">
              <span className="gtins-k">The role</span>
              <p className="gtins-big">{p.role.title}</p>
              <p className="gtivl-muted">{p.role.family}</p>
              {p.role.path && <a className="gtiv-link" href={site + p.role.path} target="_blank" rel="noopener noreferrer">Role profile on the site &#8599;</a>}
            </section>
          )}
          <section className="gtiv-card gtins-card gtins-meta">
            {p.published_at && <p>First published {ukTime(p.published_at)}</p>}
            {p.edited_by && <p>Last edited by {p.edited_by}, {ukTime(p.edited_at)}</p>}
            {p.written_at && <p>Drafted {ukTime(p.written_at)}</p>}
            {!p.published_at && <button type="button" className="gtiv-link gtins-del" onClick={remove}>Delete this draft</button>}
          </section>
        </aside>
      </div>
    </div>
  );
}

export default function GtInsights({ getToken }) {
  const authFetch = useAuthFetch(getToken);
  const navigate = useNavigate();
  const { postId } = useParams();
  if (postId === 'list') return <EmailList authFetch={authFetch} navigate={navigate} />; // C1-NEWS
  if (postId === 'linkedin' || postId === 'email') return <PostList tab={postId} authFetch={authFetch} navigate={navigate} />; // C1-INSTABS
  return postId
    ? <PostEditor key={postId} id={postId} authFetch={authFetch} navigate={navigate} />
    : <PostList authFetch={authFetch} navigate={navigate} />;
}
